import { bindPreviewDiagnostics } from '../../../../shared/preview-diagnostics';
import { FirebaseAuthService } from '../../../../shared/firebase-auth.service';
import { ClientLoggingService } from '../../../../shared/client-logging.service';
import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  AfterViewInit,
  OnInit,
  ChangeDetectorRef,
  NgZone,
  TemplateRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';
import { PdfRenderFailure, PdfRenderService } from '../../../../shared/pdf-render.service';
import { RbacService } from '../../../../shared/rbac.service';
import {
  anchoredScrollPosition,
  CssPoint,
  measureScrollport,
  ScrollPosition,
  scrollportCenter,
} from '../../../../shared/pdf-scrollport';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { PageRotationConflictError, PageRotationService } from '../../../../shared/page-rotation.service';
import { TabService } from '../../../../shared/tab.service';
import {
  HIGHLIGHT_ZOOM_QUERY_PARAM,
  HighlightRegion,
  HighlightUnits,
  NO_HIGHLIGHT_REGIONS,
  highlightQueryParams,
  parseHighlightZoom,
  unionBox,
} from '../../../../shared/highlight-regions';
import { HIGHLIGHT_PADDING_POINTS } from '../../../../shared/project-search-results';
import { markProjectSearchMilestone } from '../../../../shared/project-search-performance';
import { ScaleDetectionService } from '../../../../shared/scale-detection.service';
import { PdfToolbarComponent, PdfToolbarAction } from './pdf-toolbar/pdf-toolbar.component';
import { PreviewShortcutsDialogComponent } from './pdf-toolbar/preview-shortcuts-dialog.component';
import { isShortcutBlockedTarget, isShortcutListKey, matchPreviewShortcut } from './pdf-toolbar/preview-shortcuts';
import {
  IDLE_INTERACTION,
  PREVIEW_AREA_SHAPES,
  PreviewAreaShape,
  PreviewFitMode,
  PreviewInteraction,
  PreviewTool,
  applyAreaShape,
  applyToolCommand,
  previewCommandNeedsEditRights,
  startScaleReference,
  toolOf,
} from './pdf-toolbar/preview-commands';
import {
  BoundingBox,
  SPOTLIGHT_FADE_IN_MS,
  SPOTLIGHT_FADE_OUT_MS,
  SpotlightCutout,
  SpotlightOverlayComponent,
} from './spotlight-overlay/spotlight-overlay.component';
import { FlightWindow, SpotlightFlightPainter } from './spotlight-overlay/spotlight-flight-painter';
import { PreviewAxis, resizePreviewTransform, viewCentreOnCanvas } from './resize-refit-preview';
import { TextHighlightOverlayComponent } from './text-highlight-overlay/text-highlight-overlay.component';
import { TextSelectionOverlayComponent } from './text-selection-overlay/text-selection-overlay.component';
import {
  NormalizedTextBox,
  PageTextIndex,
  TextFocusService,
  TextSearchMatch,
  TextSearchState,
} from '../../../../shared/text-focus.service';
import { DEFAULT_FEATHER_PERCENT, resolveFeatherPx } from './spotlight-overlay/spotlight-feather';
import { RegionHierarchyTreeNode } from '../../../../../generated.commonjs/api_pb';
import { MeasureOverlayComponent, MeasureMode } from './measure-overlay/measure-overlay.component';
import { NormalizedLine } from './measure-overlay/scale-calibration';
import { AreaShape, NormalizedPoint as AreaPoint } from './measure-overlay/area-geometry';
import {
  ApplyCalibrationEvent,
  ScaleCalibrationPanelComponent,
} from './scale-calibration-panel/scale-calibration-panel.component';
import { ScaleCalibrationController } from './scale-calibration-panel/scale-calibration.controller';
import { RegionChatSelectionStore } from './region-chat/region-chat-selection.store';
import { RegionChatSurfaceComponent } from './region-chat/region-chat-surface.component';
import { RegionChatPanelComponent } from './region-chat/region-chat-panel.component';
import { PageRegionCropService } from '../../../../services/page-region-crop.service';
import { ChatStateService } from '../../../../services/chat-state.service';
import { FileMetadataService } from '../../../../shared/file-metadata.service';
import { normalizeRotation } from '../../../../shared/page-region/region-geometry';
import {
  PageRotation,
  displayRectToRotation,
  permilleYxBoxToDisplayRect,
} from '../../../../shared/page-geometry/page-geometry';
import {
  PreviewFocusService,
  FocusTargetRequest,
  FocusCancellationEvent,
  FocusSource,
  PageIdentity,
  focusFramingZoom,
} from '../../../../shared/preview-focus.service';
import {
  CameraRect,
  CameraTransitionPlan,
  CameraView,
  planCameraTransition,
  sampleCameraTransition,
} from '../../../../shared/camera-transition-planner';
import {
  BehaviorSubject,
  Subject,
  switchMap,
  finalize,
  takeUntil,
  from,
  distinctUntilChanged,
  combineLatest,
} from 'rxjs';

/** Preview tools that take over pointer input; only one can be active. */
export type { PreviewTool } from './pdf-toolbar/preview-commands';

interface FocusViewportInsets {
  top: number;
  bottom: number;
  side: number;
}

/**
 * A camera move is laid out at its widest view when that view is below this fraction of the
 * starting scale (Issue #2924). Smaller changes keep the starting layout and skip the re-layout.
 */
const FOCUS_RELAYOUT_SCALE_RATIO = 0.75;
const WIDEST_VIEW_SAMPLES = 48;

/** Returns the page scale of the widest view a planned camera move passes through. */
function widestCameraScale(plan: CameraTransitionPlan, usableViewportWidth: number): number {
  let widest = 0;
  for (const phase of plan.phases) {
    widest = Math.max(widest, phase.fromView.visibleWidth, phase.toView.visibleWidth);
  }
  // A flyover leg can widen beyond both of its ends, so sample the path between them as well.
  for (let index = 1; index < WIDEST_VIEW_SAMPLES; index++) {
    const view = sampleCameraTransition(plan, (plan.durationMs * index) / WIDEST_VIEW_SAMPLES);
    widest = Math.max(widest, view.visibleWidth);
  }
  return usableViewportWidth / widest;
}

/** PDF preview pane that renders the current plan page on a canvas and manages zoom, scroll, and resize interactions. */
@Component({
  selector: 'app-preview',
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    PdfToolbarComponent,
    SpotlightOverlayComponent,
    TextHighlightOverlayComponent,
    TextSelectionOverlayComponent,
    MeasureOverlayComponent,
    ScaleCalibrationPanelComponent,
    RegionChatSurfaceComponent,
    RegionChatPanelComponent,
  ],
  providers: [PdfRenderService, RegionChatSelectionStore, ScaleCalibrationController],
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
})
export class PreviewComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  private static readonly MAX_DISPLAY_ZOOM = 5.0;
  private static readonly MAX_DISPLAY_TO_RENDER_SCALE_RATIO = 2.84;

  private pdfRenderService = inject(PdfRenderService);
  private planReviewerService = inject(PlanReviewerService);
  private pageRotationService = inject(PageRotationService);
  private tabService = inject(TabService);
  private scaleDetectionService = inject(ScaleDetectionService);
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private rbacService = inject(RbacService);
  private textFocus = inject(TextFocusService);
  private readonly diagnosticAuth = inject(FirebaseAuthService);
  private unbindDiagnostics = () => {};
  private previewFocusService = inject(PreviewFocusService);
  private pageRegionCropService = inject(PageRegionCropService);
  private chatStateService = inject(ChatStateService);
  private fileMetadataService = inject(FileMetadataService);
  /** File display names by project and file ID, used to label region crops (Issue #1747). */
  private readonly regionFileNames = new Map<string, Map<string, string>>();
  private readonly regionFileNameLookups = new Set<string>();
  /** Select area for chat state, shared with the surface and panel (Issue #1747). */
  readonly regionChatStore = inject(RegionChatSelectionStore);
  /**
   * The scale of the page on screen and the calibration workflow (Issue #2442). The measure overlay,
   * the toolbar and the calibration panel are bound to its fields.
   */
  readonly scale = inject(ScaleCalibrationController);

  @ViewChild('pdfCanvas') pdfCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('pdfContainer') pdfContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('canvasWrapper', { read: ElementRef }) canvasWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild(PdfToolbarComponent) pdfToolbar!: PdfToolbarComponent;
  @ViewChild(SpotlightOverlayComponent) spotlightOverlay?: SpotlightOverlayComponent;
  @ViewChild(TextHighlightOverlayComponent) textHighlightOverlay?: TextHighlightOverlayComponent;
  @ViewChild(MeasureOverlayComponent) measureOverlay?: MeasureOverlayComponent;
  @ViewChild(TextSelectionOverlayComponent) textSelectionOverlay?: TextSelectionOverlayComponent;
  @ViewChild(RegionChatSurfaceComponent) regionChatSurface?: RegionChatSurfaceComponent;
  @ViewChild('findInput') findInput?: ElementRef<HTMLInputElement>;

  isLoading = false;
  pdfRenderFailure: PdfRenderFailure | null = null;
  currentScale = 1.0;
  baseScale = 1.0;
  /** The last Fit to width or Fit to height, and the zoom it produced (Issue #2542). */
  private fittedTo: { mode: 'width' | 'height'; scale: number } | null = null;
  page$ = new BehaviorSubject<ArchitecturalPlanPage | null>(null);
  private destroy$ = new Subject<void>();

  // Camera focus controller state (Issue #1748)
  /** Page scale of the layout a running camera move is drawn on, when it differs from the painted one. */
  private focusLayoutScale: number | null = null;
  focusAnnouncement = '';
  private pendingFocusTargetRequest: FocusTargetRequest | null = null;
  private focusAnimationTimeout: ReturnType<typeof setTimeout> | null = null;
  private focusAnimationFrame: number | null = null;
  private focusOriginalScrollBehavior: string | null = null;
  private lastFocusedHighlightTarget: {
    pageNumber: number;
    fileId?: string;
    box: BoundingBox;
    /** The closer framing the box was shown with (Issue #3256); absent for the standard framing. */
    framingZoom?: number;
  } | null = null;
  /** The closer framing the link asks for (Issue #3256), or `null` for the standard framing. */
  private routeHighlightZoom: number | null = null;
  private renderedPageIdentity: { pageNumber: number; fileId?: string } | null = null;
  private renderGeneration = 0;

  // Spotlight highlight support (Issue #292)
  highlightBox: BoundingBox | null = null;
  private urlHighlightBox: BoundingBox | null = null;
  /** The individual regions inside `urlHighlightBox`, with their labels (Issue #2254). */
  private urlHighlightRegions: readonly HighlightRegion[] = NO_HIGHLIGHT_REGIONS;
  /**
   * The highlight exactly as the link carries it (Issue #2445). `urlHighlightRegions` and
   * `urlHighlightBox` are derived from it for the rotation the page is rendered at, and are derived
   * again after every render, so a highlight follows the page when it is turned.
   */
  private linkHighlightRegions: readonly HighlightRegion[] = NO_HIGHLIGHT_REGIONS;
  /** The box that contains the link's regions, as announced with them. */
  private linkHighlightBox: BoundingBox | null = null;
  /**
   * The rendered rotation and page size the highlight was last computed for. A render that changes
   * neither (a zoom, a repaint) leaves the highlight alone.
   */
  private highlightGeometryKey: string | null = null;
  /** The highlight last computed from its sources, to tell it from one set by other means. */
  private derivedHighlightBox: BoundingBox | null = null;
  /** The clockwise rotation the page is rendered at, published on the canvas wrapper; null before a render. */
  renderedPageRotation: PageRotation | null = null;
  /** The rendered page's size in PDF points as `width x height`, published on the canvas wrapper. */
  renderedPageSize: string | null = null;
  /** The page rotation the link's boxes were measured at; `null` until it is known. */
  private linkHighlightRotation: PageRotation | null = null;
  /** Point-based for existing deep links; normalized for project-search links (Issue #3240). */
  private linkHighlightUnits: HighlightUnits = 'points';
  /** Whether the active route, rather than an assistant action, supplied the current highlight. */
  private routeHasHighlight = false;
  /**
   * True when `linkHighlightRotation` was not stated by the link but taken from the page as first
   * rendered. Such a value belongs to that page and is dropped when another page is opened.
   */
  private linkHighlightRotationAssumed = false;
  /** The region under the pointer in the hierarchy, kept so its box can be recomputed. */
  private hoveredRegion: RegionHierarchyTreeNode.AsObject | null = null;
  /**
   * The selected region while another row is hovered, drawn half shaded beside the hovered one
   * (Issue #3127). Null otherwise.
   */
  dimmedHighlightBox: BoundingBox | null = null;
  /** Whether the spotlight cross-fades its next change: yes for a selection, no for a hover. */
  spotlightFade = false;
  /**
   * The spotlight shading drawn per frame during a camera flight, and when it started, or null when
   * no flight is painting it (Issue #3127).
   */
  private spotlightFlight: {
    canvas: HTMLCanvasElement;
    painter: SpotlightFlightPainter;
    startedAt: number;
    origin: CssPoint;
  } | null = null;
  /** True from a flight's first frame until it lands or is cancelled. */
  private focusFlightRunning = false;
  /** Spotlight feather as a percentage of the highlighted box size (Issue #1496). */
  spotlightFeatherPct = DEFAULT_FEATHER_PERCENT;

  // OCR/PDF text search and reusable text focus (Issue #1744)
  findOpen = false;
  findQuery = '';
  textSearchState: TextSearchState = this.textFocus.state;
  textHighlightBoxes: NormalizedTextBox[] = [];
  textHighlightBoxesNormalized = false;
  private findTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingTextMatch: TextSearchMatch | null = null;
  private findReturnFocus: HTMLElement | null = null;
  // OCR/PDF text selection and chat context (Issue #1745)
  currentTextIndex: PageTextIndex | null = null;

  get currentPage(): ArchitecturalPlanPage | null {
    return this.page$.getValue();
  }

  /** Stable semantic page identity for the rendered viewer and deployed journey assertions. */
  get currentPageProjectId(): string {
    return this.currentPage?.getArchitecturalPlanId?.() ?? '';
  }

  get currentPageNumber(): number | null {
    return this.currentPage?.getPageNumber?.() ?? null;
  }

  get currentPageTitle(): string {
    return this.currentPage?.getTitle?.() ?? '';
  }

  get currentPageIdentityFileId(): string {
    const page = this.currentPage;
    return page ? (this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1') : '';
  }

  /** True only after the renderer has published completion for the currently selected page. */
  get hasRenderedCurrentPage(): boolean {
    const page = this.currentPage;
    if (!page || !this.renderedPageIdentity) return false;
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? undefined;
    return this.renderedPageIdentity.pageNumber === page.getPageNumber() && this.renderedPageIdentity.fileId === fileId;
  }

  /** Canonical business-readiness gate for the canvas and every page-dependent control. */
  get isPreviewReady(): boolean {
    return !this.isLoading && !this.pdfRenderFailure && this.hasRenderedCurrentPage;
  }

  /** Stable semantic state exposed to deployed CUJ assertions. */
  get previewRenderState(): 'empty' | 'loading' | 'error' | 'ready' {
    if (!this.currentPage) return 'empty';
    if (this.pdfRenderFailure) return 'error';
    return this.isPreviewReady ? 'ready' : 'loading';
  }

  /** The measurement mode, or null when no measurement tool is active. */
  get measureMode(): MeasureMode {
    if (this.calibrating) return 'calibrate';
    if (this.interaction.kind === 'line') return 'ruler';
    return this.interaction.kind === 'area' ? 'area' : null;
  }

  /** The shape the measure overlay draws while Select area uses one of its shapes. */
  get overlayAreaShape(): AreaShape {
    return this.interaction.kind === 'area' ? this.interaction.shape : 'rectangle';
  }

  /**
   * The measurement result's template, for this component to draw outside the scrolling page; null
   * when there is nothing to show (Issue #2542). The ruler and the area tool both show their result
   * as a panel docked in a corner of the Preview, or as a sheet along its bottom on a phone.
   *
   * The Preview's own state is checked first. In the pass in which the tool is closed, the overlay
   * has just been destroyed but is still the queried instance, and its template must not be drawn
   * one last time.
   */
  get measureResultTemplate(): TemplateRef<unknown> | null {
    const mode = this.measureMode;
    if (!mode || mode === 'calibrate' || this.isLoading) return null;
    return this.measureOverlay?.sheetTemplate ?? null;
  }

  /** True while the chat attachment of one measured area is being reviewed (Issue #2542). */
  get areaChatReview(): boolean {
    return this.interaction.kind === 'area' && !!this.interaction.chatReview;
  }

  /** True while the ruler line is the reference of a calibration being entered (Issue #2542). */
  get calibrating(): boolean {
    return this.interaction.kind === 'line' && this.interaction.operation === 'scaleReference';
  }

  get currentPageFileId(): string {
    const page = this.currentPage;
    return (page && this.extractFileIdFromPagePath(page.getPageFolderPath())) ?? '1';
  }

  // Page rotation state (Issue #1071)
  isRotating = false;

  /**
   * The geometry interaction (Issue #2542): idle, a ruler line (measuring, or the reference of a
   * calibration) or an area selection (measuring and chat). One value keeps the tools mutually
   * exclusive. The scale the tools measure with lives in `scale`; the area selection lives in
   * `regionChatStore`.
   */
  interaction: PreviewInteraction = IDLE_INTERACTION;

  /** The active geometry tool, if any. */
  get activeTool(): PreviewTool {
    return toolOf(this.interaction);
  }

  // Spotlight overlay dimensions (stored to trigger change detection)
  spotlightWrapperWidth = 0;
  spotlightWrapperHeight = 0;
  spotlightCanvasWidth = 0;
  spotlightCanvasHeight = 0;
  spotlightCanvasScale = 1;
  spotlightCanvasOffsetX = 0;
  spotlightCanvasOffsetY = 0;

  // Gesture handling properties
  private isDragging = false;
  private isMouseDown = false;
  private isZooming = false;
  private lastMousePosition = { x: 0, y: 0 };
  private lastTouchDistance = 0;
  private lastTouchCenter = { x: 0, y: 0 };
  private initialScale = 1.0;
  private initialScrollLeft = 0;
  private initialScrollTop = 0;
  private zoomTimeout: any = null;
  private isGestureZooming = false;
  private paintedScale = 1.0;
  private gestureStartScale = 1.0;
  private gestureCurrentScale = 1.0;
  private gestureRawScale = 1.0; // Undamped scale for continuous input (wheel/trackpad)
  private gestureFocalPointScreen = { x: 0, y: 0 };
  private gestureFocalPointViewport = {
    x: 0,
    y: 0,
    centerX: true,
    centerY: true,
  };
  private gestureFocalPointCanvas = { x: 0, y: 0 };
  private gestureUntransformedRect: DOMRect | null = null;
  private gestureBaseTransform = '';
  private initialTouchDistance = 0;
  private zoomAnimationFrame: number | null = null;
  private scrollBehaviorRestoreTimeout: ReturnType<typeof setTimeout> | null = null;

  // ResizeObserver for recalculating spotlight overlay on layout changes
  // (chat panel docked/undocked, TOC drawer toggled)
  private resizeObserver?: ResizeObserver;
  private spotlightDiagnosticSignature?: string;
  private readonly onPdfContainerScroll = (): void => {
    if (this.highlightBox) {
      this.updateSpotlightValues();
    }
  };
  private readonly onDocumentKeydownListener = (event: KeyboardEvent): void => this.onDocumentKeydown(event);

  // Tap detection properties
  private tapStartPosition = { x: 0, y: 0 };
  private readonly TAP_THRESHOLD = 10;
  /** When the last single tap happened (its event's timeStamp), or null after a double tap. */
  private lastTapTime: number | null = null;
  private tapTimeout: any = null;
  /** Whether the last single tap's delayed action (toggling the toolbar) has already run. */
  private lastTapToggledToolbar = false;
  private static readonly DOUBLE_TAP_MS = 300;
  /** A touch held this long on a word is a long press, which selects text natively. */
  private static readonly LONG_PRESS_MS = 500;
  /**
   * Whether Alt (Option on a Mac) is held, which makes the pointer the text tool (Issue #3338).
   * The template shows the text tool's cursor while it is held and no measuring or area tool is
   * active, so a tool started or ended with Alt still down takes effect at once.
   */
  altHeld = false;
  private readonly onAltKeyListener = (event: KeyboardEvent): void => {
    if (event.key === 'Alt') this.setAltHeld(event.type === 'keydown');
  };
  private readonly onWindowBlurListener = (): void => this.setAltHeld(false);
  /** Where and when a single touch on a word started, until it ends (Issue #3312). */
  private textTouch: { x: number; y: number; time: number } | null = null;

  ngOnInit() {
    // Ctrl/Cmd+F must cancel the browser's native Find dialog. Use the window capture phase so
    // Zone.js does not coalesce this listener into an existing passive document-keydown callback.
    window.addEventListener('keydown', this.onDocumentKeydownListener, { capture: true, passive: false });
    // Every key press reaches these, so they run outside Angular; setAltHeld re-enters it only
    // when Alt is pressed or released.
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('keydown', this.onAltKeyListener, { capture: true });
      window.addEventListener('keyup', this.onAltKeyListener, { capture: true });
      window.addEventListener('blur', this.onWindowBlurListener);
    });
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => this.refreshEditRights(page));

    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      const prevPage = this.page$.getValue();
      const isDifferentPage =
        !prevPage ||
        !page ||
        prevPage.getPageNumber() !== page.getPageNumber() ||
        prevPage.getPageFolderPath() !== page.getPageFolderPath();

      if (isDifferentPage) {
        this.lastFocusedHighlightTarget = null;
        this.renderedPageIdentity = null;
        // A region selection belongs to one page and never carries over (Issue #1747).
        if (this.interaction.kind === 'area') {
          this.interaction = IDLE_INTERACTION;
        }
        this.regionChatStore.deactivate(false);
        // A tap belongs to the page it was made on: no pending toolbar toggle, and no first half
        // of a double tap, carries over to the next page.
        this.resetTapState();
      }

      this.page$.next(page);
      void this.loadTextIndexForPage(page);
      this.triggerFocusForHighlightBoxIfReady();
    });

    this.textFocus.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.textSearchState = state;
    });

    this.textFocus.focus$.pipe(takeUntil(this.destroy$)).subscribe(match => {
      this.applyTextMatch(match);
    });

    // A fresh authenticated navigation can resolve the plan page before the
    // child route publishes its query params. Observe both prerequisites so a
    // reusable text selector is not lost merely because their startup order
    // differs between browser contexts.
    combineLatest([this.tabService.activePlanPage$, this.route.queryParams])
      .pipe(
        distinctUntilChanged(
          ([previousPage, previousParams], [currentPage, currentParams]) =>
            previousPage?.getPageFolderPath() === currentPage?.getPageFolderPath() &&
            previousParams['text'] === currentParams['text']
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([page, params]) => {
        if (page) void this.restoreTextSelector(page, params);
      });

    // Keep route origin as component state. Highlight geometry is also published for assistant
    // actions, so neither a box nor its units alone can distinguish a link from the assistant.
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.routeHasHighlight = typeof params['highlight'] === 'string';
      this.routeHighlightZoom = parseHighlightZoom(params[HIGHLIGHT_ZOOM_QUERY_PARAM]);
    });

    // Select area for chat (Issue #1747): keep the toolbar in step when the mode ends itself, and
    // reopen a region when arriving from a chat source link.
    this.regionChatStore.activeChanged$.pipe(takeUntil(this.destroy$)).subscribe(active => {
      if (active || this.interaction.kind !== 'area') return;
      if (this.interaction.chatReview) {
        // The chat review of one area closed: back to the areas, which were kept underneath.
        this.interaction = { kind: 'area', shape: this.interaction.shape };
        this.focusToolbarControl('preview-area-use-in-chat', 'preview-tools-menu');
      }
    });
    combineLatest([this.tabService.activePlanPage$, this.route.queryParams])
      .pipe(
        distinctUntilChanged(
          ([previousPage, previousParams], [currentPage, currentParams]) =>
            previousPage?.getPageFolderPath() === currentPage?.getPageFolderPath() &&
            previousParams['chatRegion'] === currentParams['chatRegion']
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([page, params]) => {
        const attachmentId = params['chatRegion'];
        if (page && typeof attachmentId === 'string' && attachmentId) {
          void this.restoreChatRegion(page, attachmentId);
        }
      });

    // Camera focus controller subscriptions (Issue #1748)
    this.previewFocusService.focusRequest$.pipe(takeUntil(this.destroy$)).subscribe(request => {
      void this.handleFocusTargetRequest(request);
    });

    this.previewFocusService.focusCancelled$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.handleFocusCancellation(event);
    });

    this.pdfRenderService.pageRendered$.pipe(takeUntil(this.destroy$)).subscribe(renderEvent => {
      this.onPageRendered(renderEvent);
    });

    this.pdfRenderService.renderFailure$.pipe(takeUntil(this.destroy$)).subscribe(failure => {
      this.pdfRenderFailure = failure;
      if (failure) this.renderedPageIdentity = null;
      this.cdr.markForCheck();
    });

    // Listen for highlight box from TabService (Issue #292)
    this.tabService.highlightBox$.pipe(takeUntil(this.destroy$)).subscribe(box => {
      this.spotlightFade = true;
      const regions = this.tabService.getHighlightRegions();
      // The service publishes the regions that make up the box. A box announced without them is
      // still a highlight of one region.
      this.linkHighlightRegions = regions.length > 0 ? regions : box ? [{ box }] : NO_HIGHLIGHT_REGIONS;
      this.linkHighlightBox = box;
      this.linkHighlightRotation = this.toPageRotation(this.tabService.getHighlightRotation());
      this.linkHighlightUnits = this.tabService.getHighlightUnits();
      this.linkHighlightRotationAssumed = false;
      this.refreshHighlightGeometry();
      if (this.highlightBox) {
        this.updateSpotlightValues();
        this.triggerFocusForHighlightBoxIfReady();
      } else {
        this.lastFocusedHighlightTarget = null;
      }
      ClientLoggingService.log('INFO', '[Preview] Highlight box from service:', this.highlightBox);
    });

    // Listen for region selection to highlight bounding box (Issue #1228)
    this.tabService.activeRegion$.pipe(takeUntil(this.destroy$)).subscribe(region => {
      this.spotlightFade = true;
      this.refreshHighlightGeometry();
      if (!this.regionToBoundingBox(region)) return;
      this.updateSpotlightValues();
      // The camera follows the selected region however it was selected (Issue #3134). Back, Forward
      // and a restored route set the region after the page has already asked for a move, with the
      // box of the region selected before. While a row is hovered, the highlight shows that row and
      // the move waits for the click. A click arrives while its own row is still hovered, so that
      // case moves at once.
      if (!this.hoveredRegion || this.hoveredRegion.path === region?.path) this.triggerFocusForHighlightBoxIfReady();
    });

    // Hover-to-highlight: temporarily show hovered region on preview
    this.tabService.hoveredRegion$.pipe(takeUntil(this.destroy$)).subscribe(region => {
      this.hoveredRegion = region;
      this.spotlightFade = false;
      this.refreshHighlightGeometry();
      this.updateSpotlightValues();
    });

    // Configurable spotlight feather (percentage of the highlighted box size)
    this.tabService.highlightFeatherPct$.pipe(takeUntil(this.destroy$)).subscribe(pct => {
      this.spotlightFeatherPct = pct;
    });
  }

  ngAfterViewInit(): void {
    this.pdfRenderService.setCanvas(this.pdfCanvas, this.pdfContainer);
    // Issue #2442: a saved calibration arrives after the page is on screen; show it when it does.
    this.scale.changed$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    this.unbindDiagnostics = bindPreviewDiagnostics({
      summary: () => this.textFocus.diagnosticSummary(),
      genuineSdkSession: () => this.diagnosticAuth.hasGenuineSdkSession(),
      cold: () => this.textFocus.resetDiagnosticIndexes(),
      referenceRaster: (encoded, page) =>
        this.textFocus.diagnosticReferenceRaster(encoded, page, this.pdfRenderService.getCurrentScale()),
    });
    this.setupGestureHandling();
    this.setupResizeObserver();

    this.page$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          // Compare by both pageNumber AND pageFolderPath to detect actual changes
          // This ensures file-aware comparison (Page 3 from File 2 ≠ Page 3 from File 3)
          return (
            prev?.getPageNumber() === curr?.getPageNumber() && prev?.getPageFolderPath() === curr?.getPageFolderPath()
          );
        }),
        switchMap(page => {
          const currentGeneration = ++this.renderGeneration;
          this.renderedPageIdentity = null;
          // A rotation assumed for a link belongs to the page it was assumed on.
          if (this.linkHighlightRotationAssumed) {
            this.linkHighlightRotation = null;
            this.linkHighlightRotationAssumed = false;
          }
          // Defer loading state change to avoid ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => {
            if (this.renderGeneration === currentGeneration) {
              this.isLoading = page !== null;
            }
          }, 0);

          return from(this.pdfRenderService.renderPdfForPage(page)).pipe(
            finalize(() => {
              setTimeout(() => {
                if (this.renderGeneration === currentGeneration) {
                  this.isLoading = false;
                  if (!page) this.renderedPageIdentity = null;
                  // Successful renders publish their identity through pageRendered$. A failed render
                  // must not make an empty canvas look business-ready or initialize page-scale UI.
                  if (!page || this.renderedPageIdentity) {
                    this.updateScaleValues();
                    // Issue #2442: detect this page's scale and load its saved calibration.
                    this.showPageScale(page);
                  }
                  // Small delay to ensure DOM is updated after canvas render
                  setTimeout(() => {
                    if (this.renderGeneration === currentGeneration) {
                      this.updateSpotlightValues();
                      this.scrollTextMatchIntoView();
                      this.drainPendingFocusTargetRequest(currentGeneration);
                    }
                  }, 50);
                }
              }, 0);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    const previewActive = this.router.url.split('?')[0].endsWith('/preview');
    if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === 'f' && previewActive) {
      event.preventDefault();
      this.openFind();
      return;
    }
    if (previewActive && this.handleToolbarShortcut(event)) return;
    if (event.key === 'Escape' && this.findOpen) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.closeFind();
    }
  }

  /**
   * Runs a toolbar command from its key (Issues #2542 and #3313), through the same handler as the
   * menu item, so a key does exactly what its menu item does. The keys are listed in the menus and
   * in the list `?` opens; both read `preview-shortcuts.ts`.
   *
   * A key is ignored with Ctrl, Cmd or Alt, which belong to the browser; while typing in a field,
   * in a dialog or in an open menu; and unless the Preview or its toolbar has focus, so a letter
   * pressed in chat or in the project drawer stays there. Returns true when the key was used.
   */
  private handleToolbarShortcut(event: KeyboardEvent): boolean {
    if (this.isLoading || !this.currentPage) return false;
    const shortcut = matchPreviewShortcut(event);
    const listKey = !shortcut && isShortcutListKey(event);
    if (!shortcut && !listKey) return false;
    if (isShortcutBlockedTarget(event.target, shortcut) || !this.keyIsForPreview(event.target)) return false;
    event.preventDefault();
    if (listKey) {
      this.openShortcutList();
    } else if (shortcut) {
      this.onToolbarAction({ type: shortcut.command, areaShape: shortcut.areaShape });
      this.cdr.markForCheck();
    }
    return true;
  }

  /** True when a key press comes from the Preview, its toolbar menus, or no focused element. */
  private keyIsForPreview(target: EventTarget | null): boolean {
    const element = target instanceof Element ? target : null;
    if (!element || element === document.body || element === document.documentElement) return true;
    const host: HTMLElement = this.hostElement.nativeElement;
    return host.contains(element) || !!element.closest('.preview-toolbar-menu');
  }

  /**
   * Whether the signed-in reviewer may edit the shown project: true for an editor or owner, false
   * for a reader or prompter, null while it is being looked up or if the lookup failed. Only a known
   * false withholds a command; when the role is unknown the server still decides.
   */
  canEditProject: boolean | null = null;
  private editRightsProjectId: string | null = null;

  /** Looks up the reviewer's role once for each project the Preview shows (Issue #3313). */
  private refreshEditRights(page: ArchitecturalPlanPage | null): void {
    // A page without a project id (no project to save against) has no role to look up.
    const projectId = typeof page?.getArchitecturalPlanId === 'function' ? page.getArchitecturalPlanId() || null : null;
    if (!projectId || projectId === this.editRightsProjectId) return;
    this.editRightsProjectId = projectId;
    this.canEditProject = null;
    this.rbacService
      .getUserProjects()
      .then(projects => {
        if (this.editRightsProjectId !== projectId) return;
        const project = (projects ?? []).find(candidate => candidate?.architecturalProjectId === projectId);
        // UserRole: READER = 1, PROMPTER = 2, EDITOR = 3, OWNER = 4 (rbac.proto); the server's
        // canEditProject needs EDITOR or above.
        this.canEditProject = project ? project.role >= 3 : null;
        this.cdr.markForCheck();
      })
      .catch(() => {
        // The role stays unknown; the server still refuses an edit a reader asks for.
      });
  }

  /** Opens the list of keyboard shortcuts (Issue #3313). */
  openShortcutList(): void {
    if (this.dialog.openDialogs.some(ref => ref.componentInstance instanceof PreviewShortcutsDialogComponent)) return;
    this.dialog.open(PreviewShortcutsDialogComponent, {
      ariaLabelledBy: 'preview-shortcuts-title',
      autoFocus: 'dialog',
      restoreFocus: true,
    });
  }

  async retryPdfPreview(): Promise<void> {
    const page = this.currentPage;
    if (!page || this.isLoading) return;

    const retryGeneration = ++this.renderGeneration;
    this.renderedPageIdentity = null;
    this.planReviewerService.invalidatePagePdfCache(page);
    this.pdfRenderService.invalidatePageCache(page);
    this.isLoading = true;
    try {
      await this.pdfRenderService.renderPdfForPage(page);
    } finally {
      // A page change starts a newer render generation. Its loading state and scale setup belong
      // to that newer request, so an older retry must not clear or initialize them when it settles.
      if (this.renderGeneration === retryGeneration) {
        this.isLoading = false;
        if (this.renderedPageIdentity) {
          this.updateScaleValues();
          this.showPageScale(page);
        }
        this.cdr.markForCheck();
      }
    }
  }

  openFind(): void {
    if (!this.findOpen) {
      const activeElement = document.activeElement;
      this.findReturnFocus =
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        activeElement !== document.documentElement
          ? activeElement
          : null;
    }
    this.findOpen = true;
    setTimeout(() => {
      this.findInput?.nativeElement.focus();
      this.findInput?.nativeElement.select();
    });
  }

  closeFind(): void {
    const returnFocus = this.findReturnFocus;
    this.findReturnFocus = null;
    this.findOpen = false;
    if (this.findTimer) {
      clearTimeout(this.findTimer);
      this.findTimer = null;
    }
    this.findQuery = '';
    this.textHighlightBoxes = [];
    this.pendingTextMatch = null;
    this.textFocus.clear();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { text: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    setTimeout(() => {
      const target = returnFocus?.isConnected ? returnFocus : this.pdfContainer?.nativeElement;
      target?.focus();
    });
  }

  async searchAgain(): Promise<void> {
    // The recovery button is removed synchronously when search emits its
    // searching state. Move focus to the persistent control before that.
    this.findInput?.nativeElement.focus();
    this.findInput?.nativeElement.select();
    await this.runTextSearch();
  }

  onFindQueryChange(query: string): void {
    this.textFocus.cancelSearch();
    this.findQuery = query;
    if (this.findTimer) clearTimeout(this.findTimer);
    this.findTimer = setTimeout(() => void this.runTextSearch(), 250);
  }

  onFindInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      void this.moveTextMatch(event.shiftKey ? -1 : 1);
    }
  }

  async moveTextMatch(delta: number): Promise<void> {
    const page = this.page$.getValue();
    if (!page) return;
    await this.textFocus.move(page.getArchitecturalPlanId(), delta);
  }

  private async runTextSearch(): Promise<void> {
    const page = this.page$.getValue();
    if (!page) return;
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1';
    await this.textFocus.search(page.getArchitecturalPlanId(), fileId, this.findQuery);
  }

  private async loadTextIndexForPage(page: ArchitecturalPlanPage | null): Promise<void> {
    if (!page) {
      this.currentTextIndex = null;
      return;
    }
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1';
    try {
      const result = await this.textFocus.getPageTextIndex(
        page.getArchitecturalPlanId(),
        fileId,
        page,
        () => this.page$.getValue() === page
      );
      if (this.page$.getValue() === page) {
        this.currentTextIndex = result.index;
      }
    } catch {
      if (this.page$.getValue() === page) {
        this.currentTextIndex = null;
      }
    }
  }

  private async restoreTextSelector(page: ArchitecturalPlanPage, queryParams?: Record<string, unknown>): Promise<void> {
    const encoded = queryParams ? queryParams['text'] : this.router.parseUrl(this.router.url).queryParams['text'];
    if (typeof encoded !== 'string') {
      this.textHighlightBoxes = [];
      this.pendingTextMatch = null;
      return;
    }
    const selector = TextFocusService.decodeSelector(encoded);
    if (!selector) {
      this.textSearchState = {
        availability: 'stale',
        query: '',
        matches: [],
        activeIndex: -1,
        message: 'This text link is invalid or uses an unsupported version.',
      };
      this.findOpen = true;
      return;
    }
    this.findOpen = true;
    this.findQuery = selector.exact;
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1';
    await this.textFocus.focusSelector(
      page.getArchitecturalPlanId(),
      fileId,
      page,
      selector,
      () => this.page$.getValue() === page
    );
  }

  private applyTextMatch(match: TextSearchMatch | null): void {
    const page = this.page$.getValue();
    const currentFileId = page ? (this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1') : null;
    if (!match || !page || match.pageNumber !== page.getPageNumber() || match.fileId !== currentFileId) {
      this.textHighlightBoxes = [];
      this.pendingTextMatch = null;
      return;
    }
    this.pendingTextMatch = match;
    this.textHighlightBoxes = match.boxes;
    this.textHighlightBoxesNormalized = match.normalizedBoxes;

    if (match.boxes.length > 0) {
      const source: FocusSource = this.router.url.includes('text=')
        ? 'deepLink'
        : this.findOpen
          ? 'search'
          : 'assistant';
      void this.previewFocusService.focusTarget({
        pageIdentity: {
          pageNumber: match.pageNumber,
          fileId: match.fileId,
        },
        geometry: { kind: 'boxes', boxes: match.boxes },
        highlightKind: 'text',
        source,
      });
    } else {
      setTimeout(() => this.scrollTextMatchIntoView());
    }
  }

  private scrollTextMatchIntoView(): void {
    const match = this.pendingTextMatch;
    const container = this.pdfContainer?.nativeElement;
    if (!match?.boxes.length || !container) return;
    const box = match.boxes[0];
    const x =
      (match.normalizedBoxes ? box.x * this.spotlightCanvasWidth : box.x * this.spotlightCanvasScale) +
      this.spotlightCanvasOffsetX;
    const y =
      (match.normalizedBoxes ? box.y * this.spotlightCanvasHeight : box.y * this.spotlightCanvasScale) +
      this.spotlightCanvasOffsetY;
    const reduceMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollLeft = Math.max(0, x - container.clientWidth / 2);
    const scrollTop = Math.max(0, y - container.clientHeight / 2);
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    } else {
      container.scrollLeft = scrollLeft;
      container.scrollTop = scrollTop;
    }
  }

  onToolbarAction(action: PdfToolbarAction): void {
    // A reader may not rotate or calibrate (Issue #3313). The menu shows those items disabled and a
    // key reaches this same check, so neither starts a change the server would refuse.
    if (this.canEditProject === false && previewCommandNeedsEditRights(action.type)) return;
    this.previewFocusService.cancelCurrentFocus('User toolbar interaction');
    switch (action.type) {
      case 'zoomIn':
        this.zoomIn();
        break;
      case 'zoomOut':
        this.zoomOut();
        break;
      case 'fitToWidth':
        this.fitToWidth();
        break;
      case 'fitToHeight':
        this.fitToHeight();
        break;
      case 'fitToViewport':
        this.fitToViewport();
        break;
      case 'openInNewTab':
        this.openInNewTab();
        break;
      case 'setZoom':
        if (action.value !== undefined) {
          this.applySmoothZoom(action.value);
        }
        break;
      case 'rotateLeft':
        this.rotatePage(270);
        break;
      case 'rotateRight':
        this.rotatePage(90);
        break;
      case 'toggleRuler':
      case 'toggleArea':
      case 'exitTool':
        this.setInteraction(applyToolCommand(this.interaction, action.type, this.areaShape));
        break;
      case 'selectAreaShape':
        if (action.areaShape) this.selectAreaShape(action.areaShape);
        break;
      case 'calibrateScale':
        if (this.calibrating) {
          this.setInteraction({ kind: 'line', operation: 'measure' });
        } else {
          this.startCalibration(this.scale.toolbarStatus === 'manual' || this.scale.toolbarStatus === 'invalidated');
        }
        break;
      case 'showScaleDetails':
        this.showScaleDetails();
        break;
      case 'toggleScaleCompare':
        this.scale.toggleCompare();
        break;
      case 'resetScale':
        this.resetScaleCalibration();
        break;
    }
  }

  /**
   * Moves to another geometry interaction (Issues #1212, #1747, #2442 and #2542).
   *
   * Leaving calibration discards its draft. Chat review activates the region store for one
   * supplied area outline; leaving review releases any unattached crop.
   */
  private setInteraction(next: PreviewInteraction): void {
    const previous = this.interaction;
    const wasCalibrating = previous.kind === 'line' && previous.operation === 'scaleReference';
    const willCalibrate = next.kind === 'line' && next.operation === 'scaleReference';
    if (wasCalibrating && !willCalibrate) {
      this.scale.cancelCalibration();
    } else if (!willCalibrate && next.kind !== previous.kind) {
      this.scale.forgetPresetLine();
    }
    this.interaction = next;
    if (next.kind !== 'idle') this.scale.closeDetails();
    // Chat only reviews an outline provided by the area tool.
    const usesRegionStore = next.kind === 'area' && !!next.chatReview;
    if (usesRegionStore) {
      const page = this.page$.getValue();
      if (page) this.updateRegionChatContext(page);
      this.regionChatStore.activate();
    } else {
      this.regionChatStore.deactivate(previous.kind === 'area' && next.kind !== 'area');
    }
    ClientLoggingService.log('INFO', `[Preview] Interaction: ${JSON.stringify(next)}`);
  }

  // ── Manual page-scale calibration (Issue #2442) ───────────────

  /**
   * Detects the scale of the page that was just rendered and loads its saved calibration.
   *
   * The detected scale comes from the page transcript and is available at once. The saved
   * calibration arrives a moment later and, when there is one, takes precedence.
   */
  private showPageScale(page: ArchitecturalPlanPage | null): void {
    // A calibration belongs to one page; a draft does not follow the reviewer to the next one.
    if (this.calibrating) this.setInteraction(IDLE_INTERACTION);
    if (!page) {
      void this.scale.showPage(null, { scaleText: null, scaleFactor: null }, 1);
      return;
    }
    const detected = this.scaleDetectionService.detectScale(page);
    // Issue #1151: correction factor for scanned PDFs with bogus MediaBox.
    const correctionFactor = page.getPageSizeCorrectionFactor?.() ?? 1;
    ClientLoggingService.log(
      'INFO',
      `[Preview] Scale detected: ${detected.scaleText ?? 'none'} (factor=${detected.scaleFactor}, correctionFactor=${correctionFactor})`
    );
    // A page without a project cannot have a saved calibration; it still measures with its
    // detected scale.
    const projectId = page.getArchitecturalPlanId?.() ?? '';
    void this.scale.showPage(
      projectId
        ? {
            projectId,
            fileId: this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1',
            pageNumber: page.getPageNumber(),
          }
        : null,
      { scaleText: detected.scaleText, scaleFactor: detected.scaleFactor },
      correctionFactor
    );
  }

  /**
   * Page → Calibrate (Issue #2542): the ruler, with its line used as the scale reference. There is
   * no separate calibration drawing mode.
   *
   * @param fromSaved start from the saved reference line, for recalibrating
   */
  private startCalibration(fromSaved: boolean): void {
    this.setInteraction(startScaleReference());
    this.scale.beginCalibration(fromSaved);
  }

  /** Starts a new calibration from the saved reference line. */
  private startRecalibration(): void {
    this.startCalibration(true);
  }

  // ── Select area shapes and chat (Issues #2439, #2440, #1747 and #2542) ─────

  /** The shape Select area starts with: the one the reviewer used last. */
  areaShape: PreviewAreaShape = this.loadAreaShapePreference();

  /** Starts or continues Select area with the shape chosen in the Tools menu. */
  private selectAreaShape(shape: PreviewAreaShape): void {
    // Select & move acts on areas that already exist; it is not a shape to start the tool with.
    if (shape !== 'select') {
      this.areaShape = shape;
      try {
        localStorage.setItem('preview-area-shape', shape);
      } catch {
        // The selected shape still works when browser storage is unavailable.
      }
    }
    this.setInteraction(applyAreaShape(shape));
  }

  private loadAreaShapePreference(): PreviewAreaShape {
    try {
      const stored = localStorage.getItem('preview-area-shape');
      if (stored === 'square') return 'rectangle';
      if (stored === 'circle') return 'ellipse';
      const known = PREVIEW_AREA_SHAPES.find(option => option.shape === stored && option.shape !== 'select');
      if (known) return known.shape;
    } catch {
      // Use Rectangle when browser storage is unavailable.
    }
    return 'rectangle';
  }

  /**
   * Escape cleared every finished area (Issue #2542). One key press removed work that may have taken
   * a while to draw, so the Preview says what happened and offers to undo it.
   */
  onAreasCleared(count: number): void {
    const cleared = count === 1 ? 'Cleared 1 area' : `Cleared ${count} areas`;
    this.snackBar
      .open(cleared, 'Undo', { duration: 8000 })
      ?.onAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.measureOverlay?.undoAreaChange());
  }

  /**
   * "Use in chat" on one measured area (Issue #2542): its outline goes to the chat attachment
   * workflow of Issue #1747, which shows the exact image before anything is attached. The areas
   * stay on screen underneath and cannot be edited until the review closes.
   */
  onAreaUseInChat(outline: AreaPoint[]): void {
    if (this.interaction.kind !== 'area' || outline.length < 3) return;
    this.setInteraction({ ...this.interaction, chatReview: true });
    this.regionChatStore.reviewOutline(outline);
  }

  /**
   * "Use as scale reference" on a finished ruler measurement (Issue #2542): the same line becomes the
   * calibration reference, so the reviewer does not draw it again.
   */
  onUseAsScaleReference(line: NormalizedLine): void {
    this.setInteraction(startScaleReference());
    this.scale.beginCalibrationFromLine(line);
  }

  private showScaleDetails(): void {
    if (this.calibrating) this.setInteraction({ kind: 'line', operation: 'measure' });
    void this.scale.showDetails();
  }

  /** The reviewer traced or moved the calibration line on the overlay. */
  onCalibrationLineChange(line: NormalizedLine | null): void {
    this.scale.onLineTraced(line);
  }

  /**
   * Where the calibration panel is docked; null while it is closed.
   *
   * The panel does not float over the drawing. While it is open the drawing area gives up a
   * column beside it, or on a phone a band below it, so the panel can never cover the line being
   * traced or its handles.
   */
  get scalePanelDock(): 'left' | 'right' | null {
    return !this.isLoading && this.scale.panelView ? this.scale.panelPlacement : null;
  }

  private lastScalePanelDock: 'left' | 'right' | null = null;

  ngAfterViewChecked(): void {
    const dock = this.scalePanelDock;
    if (dock === this.lastScalePanelDock) return;
    this.lastScalePanelDock = dock;
    this.refitAfterDrawingAreaChanged();
  }

  /**
   * Fits the page to the drawing area again after the calibration panel took or returned its
   * space, but only when the page was fitted before. A reviewer who has zoomed in on a dimension
   * keeps that zoom.
   */
  private refitAfterDrawingAreaChanged(): void {
    if (Math.abs(this.currentScale - this.baseScale) > 0.001) return;
    setTimeout(() => void this.fitToViewport());
  }

  /** The reviewer left calibration from the panel without saving. */
  onCancelCalibration(): void {
    if (this.calibrating) this.setInteraction({ kind: 'line', operation: 'measure' });
    this.focusAfterCalibrationClosed();
  }

  /**
   * Where focus goes when a calibration closes without saving: back to "Use as scale reference"
   * when the ruler line it started from is still shown, otherwise to the Tools trigger, which
   * names the ruler that is still active. Focus must not fall back to the page.
   */
  private focusAfterCalibrationClosed(): void {
    this.focusToolbarControl('preview-ruler-use-as-reference', 'preview-tools-menu');
  }

  /** The reviewer closed the calibration details. */
  onCloseScaleDetails(): void {
    this.scale.closeDetails();
    this.focusToolbarControl('preview-page-menu');
  }

  /**
   * Returns keyboard focus to the toolbar control an interaction started from, so that focus does
   * not fall back to the page when the calibration panel closes. The first control that exists
   * takes the focus.
   */
  private focusToolbarControl(...testIds: string[]): void {
    setTimeout(() => {
      const host: HTMLElement = this.hostElement.nativeElement;
      for (const testId of testIds) {
        const control = host.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
        if (control && !(control as HTMLButtonElement).disabled) {
          control.focus({ preventScroll: true });
          return;
        }
      }
    });
  }

  /**
   * Places a calibration line in the part of the page that is on screen and moves focus to its
   * first handle, so it can be adjusted with the arrow keys without pointing at the page.
   */
  onPlaceCalibrationLine(): void {
    const canvas = this.pdfCanvas?.nativeElement.getBoundingClientRect();
    const container = this.pdfContainer?.nativeElement.getBoundingClientRect();
    const visible =
      canvas && container && canvas.width > 0 && canvas.height > 0
        ? {
            left: (container.left - canvas.left) / canvas.width,
            top: (container.top - canvas.top) / canvas.height,
            right: (container.right - canvas.left) / canvas.width,
            bottom: (container.bottom - canvas.top) / canvas.height,
          }
        : { left: 0, top: 0, right: 1, bottom: 1 };
    this.scale.placeLine(visible);
    this.cdr.detectChanges();
    this.pdfContainer?.nativeElement
      .querySelector<HTMLElement>('[data-testid="calibration-handle-start"]')
      ?.focus({ preventScroll: true });
  }

  /** Discards the traced line so a new one can be drawn. */
  onRedrawCalibrationLine(): void {
    this.scale.redrawLine();
    // The overlay holds the line that is on screen. Without clearing it too, the old line stays
    // and the overlay ignores the next clicks, because it protects a finished line from them.
    this.measureOverlay?.redrawCalibrationLine();
  }

  /**
   * Saves the calibration. When it is saved the ruler takes over with the reference line still on
   * screen, so the reviewer immediately sees that line read the length they entered.
   */
  async onApplyCalibration(event: ApplyCalibrationEvent): Promise<void> {
    const result = await this.scale.apply(event);
    if (result.applied) {
      // The ruler measures again, with the reference line on screen reading the entered length.
      this.interaction = { kind: 'line', operation: 'measure' };
      this.snackBar.open(result.notice, 'OK', { duration: 6000 });
      // Focus returns to the ruler result, which now reads the entered length (Issue #2542).
      this.cdr.detectChanges();
      if (this.measureOverlay) {
        this.measureOverlay.focusResult();
      } else {
        this.focusToolbarControl('preview-page-menu');
      }
    }
    this.cdr.markForCheck();
  }

  /** Removes the saved calibration and tells the reviewer what the page uses now. */
  async resetScaleCalibration(): Promise<void> {
    if (this.calibrating) this.setInteraction({ kind: 'line', operation: 'measure' });
    const result = await this.scale.reset();
    if (result.notice) {
      this.snackBar.open(result.notice, 'OK', { duration: 6000 });
    }
    this.cdr.markForCheck();
  }

  /** Starts recalibrating from the details panel. */
  onRecalibrateFromDetails(): void {
    this.startRecalibration();
  }

  /**
   * Tells the Select area for chat store which page is on screen and how it is turned (Issue #1747).
   * Called after every render, so a rotation or page swap reaches the stored geometry's conversions.
   */
  private updateRegionChatContext(page: ArchitecturalPlanPage): void {
    const projectId = page.getArchitecturalPlanId();
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1';
    const knownFileName = this.regionFileNames.get(projectId)?.get(fileId);
    if (!knownFileName) {
      this.loadRegionFileNames(projectId);
    }
    const chatContext = this.chatStateService.currentState?.context;
    const fileName =
      knownFileName || (chatContext?.fileId === fileId ? chatContext?.fileName : undefined) || `File ${fileId}`;
    // The page ID is the sheet number (e.g. CE-4) and the title the sheet name; together they are how
    // a reviewer refers to a drawing, so they travel with the crop as its description.
    const sheetLabel = [page.getPageId?.(), page.getTitle?.()]
      .map(part => (part ?? '').trim())
      .filter(part => part.length > 0)
      .join(' · ');
    this.regionChatStore.setPageContext({
      projectId,
      fileId,
      fileName,
      pageNumber: page.getPageNumber(),
      rotation: normalizeRotation(this.pdfRenderService.getPageRotation()),
      pageSize: this.pdfRenderService.getUnrotatedPageSize(),
      sheetLabel: sheetLabel || undefined,
    });
  }

  /**
   * Loads the project's file names once, so region crops are labelled with the real file name rather
   * than its ID. Until they arrive the label falls back to the chat context or "File <id>".
   */
  private loadRegionFileNames(projectId: string): void {
    if (!projectId || this.regionFileNameLookups.has(projectId)) return;
    this.regionFileNameLookups.add(projectId);
    this.fileMetadataService
      .listFileMetadata(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const names = new Map<string, string>();
          for (const file of response?.getFilesList?.() ?? []) {
            const id = file.getFileId();
            const name = file.getFileName();
            if (id && name) names.set(id, name);
          }
          this.regionFileNames.set(projectId, names);
          const page = this.page$.getValue();
          if (
            page &&
            page.getArchitecturalPlanId() === projectId &&
            (this.regionChatStore.active || this.regionChatStore.restored)
          ) {
            this.updateRegionChatContext(page);
          }
        },
        error: () => {
          this.regionFileNameLookups.delete(projectId);
          ClientLoggingService.log('WARN', '[Preview] File names are unavailable for region crop labels');
        },
      });
  }

  /** Reopens a region captured into chat, reporting a changed source page clearly (Issue #1747). */
  private async restoreChatRegion(page: ArchitecturalPlanPage, attachmentId: string): Promise<void> {
    try {
      const status = await this.pageRegionCropService.getSourceStatus(attachmentId);
      const source = status.source;
      const current = this.page$.getValue();
      if (!source || !current || current.getPageFolderPath() !== page.getPageFolderPath()) return;
      const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? '1';
      if (source.pageNumber !== page.getPageNumber() || source.fileId !== fileId) return;
      this.updateRegionChatContext(page);
      this.regionChatStore.restore({
        attachmentId,
        source,
        stale: status.stale || !status.pageAvailable,
        message: status.message,
      });
    } catch (error) {
      this.snackBar.open(
        error instanceof Error && error.message ? error.message : 'The region from chat could not be shown.',
        'Close',
        { duration: 5000 }
      );
    }
    this.cdr.markForCheck();
  }

  /** Clear measurement and deactivate tool. Issue #1212. */
  onMeasurementClear(): void {
    if (this.calibrating) {
      // Escape during calibration closes the calibration and keeps the ruler (Issue #2542 Escape
      // order): the draft is discarded and nothing changes (Issue #2442).
      this.setInteraction({ kind: 'line', operation: 'measure' });
      this.focusAfterCalibrationClosed();
      return;
    }
    this.setInteraction(IDLE_INTERACTION);
    this.focusToolbarControl('preview-tools-menu');
  }

  private getMaximumZoomScale(): number {
    const maximumRenderScale = this.pdfRenderService.getMaximumRenderScale();
    const adaptiveDisplayMaximum = Math.min(
      PreviewComponent.MAX_DISPLAY_ZOOM,
      maximumRenderScale * PreviewComponent.MAX_DISPLAY_TO_RENDER_SCALE_RATIO
    );
    return Math.max(this.baseScale, this.pdfRenderService.getSafeScale(adaptiveDisplayMaximum));
  }

  private async zoomIn(): Promise<void> {
    const maxScale = this.getMaximumZoomScale();
    if (this.gestureCurrentScale >= maxScale) {
      // At boundary: trigger a visual bounce to indicate the limit
      this.triggerBounce(maxScale * 1.06);
      return;
    }
    const newScale = Math.min(maxScale, this.gestureCurrentScale * 1.2);
    this.applySmoothZoom(newScale);
  }

  private async zoomOut(): Promise<void> {
    const minScale = this.baseScale;
    if (this.gestureCurrentScale <= minScale) {
      // At boundary: trigger a visual bounce to indicate the limit
      this.triggerBounce(minScale * 0.92);
      return;
    }
    const newScale = Math.max(minScale, this.gestureCurrentScale / 1.2);
    // When zooming out exactly to baseScale, treat as a fit-to-viewport operation
    // to ensure scroll centering and proper state alignment.
    const isFitToScreen = Math.abs(newScale - minScale) < 0.001;
    this.applySmoothZoom(newScale, undefined, undefined, isFitToScreen);
  }

  /** Apply logarithmic rubber-band resistance to a raw scale value. */
  private applyRubberBand(rawScale: number): number {
    const minScale = this.baseScale;
    const maxScale = this.getMaximumZoomScale();
    if (rawScale < minScale) {
      const overshoot = minScale - rawScale;
      const maxElastic = minScale * 0.5; // Allow visually shrinking to 50% of minScale
      return minScale - maxElastic * (1 - Math.exp(-overshoot / (minScale * 0.8)));
    } else if (rawScale > maxScale) {
      const overshoot = rawScale - maxScale;
      const maxElastic = maxScale * 0.3; // Allow visually growing to 130% of maxScale
      return maxScale + maxElastic * (1 - Math.exp(-overshoot / (maxScale * 0.5)));
    }
    return rawScale;
  }

  private recordGestureFocalPoint(container: HTMLDivElement, focalXScreen?: number, focalYScreen?: number): void {
    const scrollport = measureScrollport(container);
    const center = scrollportCenter(container);
    const target = {
      x: focalXScreen ?? center.x,
      y: focalYScreen ?? center.y,
    };
    this.gestureFocalPointScreen = target;
    this.gestureFocalPointViewport = {
      x: target.x - scrollport.left,
      y: target.y - scrollport.top,
      centerX: focalXScreen === undefined,
      centerY: focalYScreen === undefined,
    };
  }

  /**
   * Resolves the gesture's intended screen anchor against the current
   * scrollport. Toolbar zoom follows a resized viewport center, while pointer
   * and touch anchors retain their local position if a drawer or panel moves.
   */
  private resolveGestureFocalPointScreen(): CssPoint {
    const container = this.pdfContainer?.nativeElement;
    if (!container) return this.gestureFocalPointScreen;

    const scrollport = measureScrollport(container);
    return {
      x: this.gestureFocalPointViewport.centerX
        ? scrollport.left + scrollport.width / 2
        : scrollport.left + Math.max(0, Math.min(scrollport.width, this.gestureFocalPointViewport.x)),
      y: this.gestureFocalPointViewport.centerY
        ? scrollport.top + scrollport.height / 2
        : scrollport.top + Math.max(0, Math.min(scrollport.height, this.gestureFocalPointViewport.y)),
    };
  }

  /** Animate a quick bounce: overshoot then spring back to target.
   *  This is purely cosmetic — the actual zoom level doesn't change.
   *  Phase 1 applies a CSS scale overshoot, phase 2 animates back to identity.
   */
  private triggerBounce(overshootScale: number): void {
    if (!this.pdfCanvas?.nativeElement || !this.pdfContainer?.nativeElement) return;
    const canvas = this.pdfCanvas.nativeElement;
    const container = this.pdfContainer.nativeElement;

    // Compute the focal point at the center of the viewport
    const center = scrollportCenter(container);
    const canvasRect = canvas.getBoundingClientRect();
    const cx = center.x - canvasRect.left;
    const cy = center.y - canvasRect.top;

    // Phase 1: quick CSS overshoot
    const overshootRatio = overshootScale / this.paintedScale;
    canvas.style.transformOrigin = `${cx}px ${cy}px`;
    canvas.style.transition = 'transform 60ms ease-out';
    canvas.style.transform = `scale3d(${overshootRatio}, ${overshootRatio}, 1)`;
    this.syncSpotlightGestureTransform();

    // Phase 2: animate back to identity (no displacement, just scale back to 1:1)
    if (this.zoomTimeout) clearTimeout(this.zoomTimeout);
    this.zoomTimeout = setTimeout(() => {
      canvas.style.transition = 'transform 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      canvas.style.transform = '';
      this.syncSpotlightGestureTransform();

      // Clean up after the spring-back animation completes
      setTimeout(() => {
        canvas.style.transition = '';
        canvas.style.transformOrigin = '';
        // Transform is gone, so the stencil is back on real geometry (#1489)
        this.syncSpotlightGestureTransform();
        this.updateSpotlightValues();
      }, 190);
    }, 70);
  }

  private applySmoothZoom(
    targetScale: number,
    focalXScreen?: number,
    focalYScreen?: number,
    isFitToScreenUnzoom = false,
    isBounceBack = false
  ): void {
    if (!this.pdfCanvas?.nativeElement || !this.pdfContainer?.nativeElement) return;
    targetScale = this.pdfRenderService.getSafeScale(targetScale);
    const canvas = this.pdfCanvas.nativeElement;
    const container = this.pdfContainer.nativeElement;

    // ── Bounce-back: animate the current rubber-banded CSS transform back to the limit ──
    // When the user pinches past a zoom limit (below baseScale or above 5x),
    // setGestureZoom() applies a CSS transform like scale3d(0.7, 0.7, 1).
    // On release, we animate back to the clamped target scale, then commit.
    // Removed early-return bounce-back block. Bounce-back now uses the normal unified transition logic below.

    this.gestureCurrentScale = targetScale;

    // We only set the focal point once per zoom sequence
    if (!this.isGestureZooming) {
      this.cancelResizeRefit();
      this.pdfRenderService.cancelCurrentRender();
      this.isGestureZooming = true;
      this.gestureStartScale = this.paintedScale;

      // Temporarily strip transforms to get the true unscaled DOM rect.
      // Doing this only once at the start of a gesture sequence protects against
      // rapid-click race conditions without causing stutter during smooth animations.
      const originalTransform = canvas.style.transform;
      const originalOrigin = canvas.style.transformOrigin;
      const originalTransition = canvas.style.transition;

      canvas.style.transition = 'none';
      canvas.style.transform = '';
      canvas.style.transformOrigin = '';

      const startCanvasRect = canvas.getBoundingClientRect();
      this.gestureUntransformedRect = startCanvasRect;

      canvas.style.transform = originalTransform;
      canvas.style.transformOrigin = originalOrigin;
      canvas.style.transition = originalTransition;

      this.recordGestureFocalPoint(container, focalXScreen, focalYScreen);
      const target = this.gestureFocalPointScreen;
      this.gestureFocalPointCanvas = {
        x: target.x - startCanvasRect.left,
        y: target.y - startCanvasRect.top,
      };
    }
    this.gestureFocalPointScreen = this.resolveGestureFocalPointScreen();

    // Save current layout and visual state
    const originalWidth = canvas.style.width;
    const originalHeight = canvas.style.height;
    const originalScrollLeft = container.scrollLeft;
    const originalScrollTop = container.scrollTop;
    const originalScrollBehavior = container.style.scrollBehavior;

    // We used to strip transforms here to read getBoundingClientRect(), which forced a massive
    // synchronous reflow and caused a UI freeze during pinch bounce-back. Now we use the cached rect.
    const untransformedCanvasRect = this.gestureUntransformedRect!;

    // Temporarily strip transforms so simulation measurements reflect pure layout positions,
    // not the prior gesture transform (review feedback: Copilot line 412).
    const savedTransform = canvas.style.transform;
    const savedOrigin = canvas.style.transformOrigin;
    const savedTransition = canvas.style.transition;
    canvas.style.transition = 'none';
    canvas.style.transform = '';
    canvas.style.transformOrigin = '';

    const scaleRatio = targetScale / this.gestureStartScale;
    const targetW = untransformedCanvasRect.width * scaleRatio;
    const targetH = untransformedCanvasRect.height * scaleRatio;

    let translateX = 0;
    let translateY = 0;
    let finalCanvasRect!: DOMRect;

    try {
      // Temporarily apply target dimensions
      canvas.style.width = `${targetW}px`;
      canvas.style.height = `${targetH}px`;

      // Allow the browser to determine exact clamped scroll positions
      container.style.scrollBehavior = 'auto';
      if (isFitToScreenUnzoom) {
        // Mirror pdfRenderService.centerView() exactly:
        // Only scroll when canvas exceeds container; otherwise margin:auto centers it.
        const scrollport = measureScrollport(container);
        const paddingOffset = PdfRenderService.CENTERING_PADDING_PX;
        if (targetW > scrollport.width) {
          container.scrollLeft = Math.max(0, (targetW - scrollport.width) / 2 + paddingOffset);
        } else {
          container.scrollLeft = 0;
        }
        if (targetH > scrollport.height) {
          container.scrollTop = Math.max(0, (targetH - scrollport.height) / 2 + paddingOffset);
        } else {
          container.scrollTop = 0;
        }

        ClientLoggingService.log('DEBUG', '[Zoom] fitToScreen simulation:', {
          targetW: Math.round(targetW),
          targetH: Math.round(targetH),
          containerW: Math.round(scrollport.width),
          containerH: Math.round(scrollport.height),
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
          canvasWiderThanContainer: targetW > scrollport.width,
          canvasTallerThanContainer: targetH > scrollport.height,
        });
      } else {
        const anchored = anchoredScrollPosition(
          container,
          canvas,
          this.gestureFocalPointCanvas,
          this.gestureFocalPointScreen,
          scaleRatio
        );

        container.scrollLeft = anchored.left;
        container.scrollTop = anchored.top;
      }

      // Measure final exact on-screen coordinates
      finalCanvasRect = canvas.getBoundingClientRect();
    } finally {
      // Restore raw layout back to its starting state.
      // NOTE: Do NOT restore scrollBehavior here — CSS scroll-behavior:smooth
      // would cause an async scroll animation that drifts the canvas during
      // the CSS transform animation. We restore it after the full swap completes.
      canvas.style.width = originalWidth;
      canvas.style.height = originalHeight;
      container.scrollLeft = originalScrollLeft;
      container.scrollTop = originalScrollTop;
    }

    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout);
    }

    const animDurationMs = isBounceBack ? 200 : 150;
    const animEasing = isBounceBack ? 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'ease-out';

    // Restore the starting transform so the CSS transition begins exactly where the user left off.
    // This is critical for bounce-backs to prevent an inorganic "zoom in before zoom out" jump.
    canvas.style.transform = savedTransform;
    canvas.style.transformOrigin = savedOrigin;
    // Put the stencil on the same start state before the reflow below, so its
    // transition animates from where the user left off too (#1489).
    this.syncSpotlightGestureTransform();
    // Force reflow so the browser registers the start state before animating.
    // This flushes pending style changes document-wide, the stencil included.
    void canvas.offsetWidth;

    // Calculate translation to perfectly preserve the focal point on screen (or center it if unzooming).
    const cx = this.gestureFocalPointCanvas.x;
    const cy = this.gestureFocalPointCanvas.y;
    translateX = finalCanvasRect.left - untransformedCanvasRect.left + cx * (scaleRatio - 1);
    translateY = finalCanvasRect.top - untransformedCanvasRect.top + cy * (scaleRatio - 1);

    canvas.style.transformOrigin = `${cx}px ${cy}px`;
    canvas.style.transition = `transform ${animDurationMs}ms ${animEasing}`;
    canvas.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale3d(${scaleRatio}, ${scaleRatio}, 1)`;
    // Hand the stencil the identical target transform + transition so it runs the
    // same animation, including the bounce-back easing (#1489).
    this.syncSpotlightGestureTransform();

    this.zoomTimeout = setTimeout(() => {
      this.isGestureZooming = false;
      canvas.style.transition = 'none';
      this.syncSpotlightGestureTransform();

      if (isFitToScreenUnzoom) {
        ClientLoggingService.log('DEBUG', '[Zoom] fitToScreen pre-swap:', {
          transform: canvas.style.transform,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
        });
        this.pdfRenderService
          .fitToViewport(() => {
            // Before the new canvas is painted: clear scroll and transform.
            container.scrollLeft = 0;
            container.scrollTop = 0;
            canvas.style.transform = '';
            canvas.style.transformOrigin = '';
            // Same frame: drop the mirrored transform and re-measure the stencil
            // against the freshly sized canvas, so there is no snap (#1489).
            this.syncSpotlightGestureTransform();
            this.updateSpotlightValues();
          })
          .then(() => {
            // Restore smooth scrolling now that the animation lifecycle is fully complete
            container.style.scrollBehavior = originalScrollBehavior;
            ClientLoggingService.log('DEBUG', '[Zoom] fitToScreen post-swap:', {
              scrollLeft: container.scrollLeft,
              scrollTop: container.scrollTop,
            });
            this.updateScaleValues();
          })
          .catch(() => {
            // If fitToViewport returns early (e.g. no PDF page loaded),
            // ensure transforms are always cleaned up to avoid a stuck state.
            canvas.style.transform = '';
            canvas.style.transformOrigin = '';
            this.syncSpotlightGestureTransform();
            this.updateSpotlightValues();
            container.style.scrollBehavior = originalScrollBehavior;
          });
      } else {
        const settledFocalPoint = this.resolveGestureFocalPointScreen();
        this.commitGestureZoom(
          targetScale,
          settledFocalPoint.x,
          settledFocalPoint.y,
          this.gestureFocalPointCanvas.x,
          this.gestureFocalPointCanvas.y
        );
        // Restore smooth scrolling now that the animation lifecycle is fully complete
        container.style.scrollBehavior = originalScrollBehavior;
      }
    }, animDurationMs);
  }

  private async fitToWidth(): Promise<void> {
    await this.pdfRenderService.fitToWidth();
    this.updateScaleValues();
    this.paintedScale = this.currentScale;
    this.fittedTo = { mode: 'width', scale: this.currentScale };
  }

  private async fitToHeight(): Promise<void> {
    await this.pdfRenderService.fitToHeight();
    this.updateScaleValues();
    this.paintedScale = this.currentScale;
    this.fittedTo = { mode: 'height', scale: this.currentScale };
  }

  private async fitToViewport(onBeforeSwap?: () => void): Promise<void> {
    await this.pdfRenderService.fitToViewport(onBeforeSwap);
    this.updateScaleValues();
    this.paintedScale = this.currentScale;
    this.fittedTo = null;
  }

  /**
   * The fit mode the page is fitted to right now, for the checked item in the Zoom menu
   * (Issue #2542). Fit to width or height holds only while the zoom is still the one that command
   * produced; any other zoom at the fitted scale is Fit to viewport; anything else is no fit.
   */
  get fitMode(): PreviewFitMode | null {
    if (this.fittedTo && Math.abs(this.currentScale - this.fittedTo.scale) < 0.001) return this.fittedTo.mode;
    return Math.abs(this.currentScale - this.baseScale) < 0.01 ? 'viewport' : null;
  }

  private async setZoom(scale: number): Promise<void> {
    this.applySmoothZoom(scale);
  }

  // ── Camera Focus Controller (Issue #1748) ─────────────────────────

  private isSameBox(
    a: { x: number; y: number; width: number; height: number } | null | undefined,
    b: { x: number; y: number; width: number; height: number } | null | undefined
  ): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
  }

  private isSameHighlightTarget(
    targetA: { pageNumber: number; fileId?: string; box: BoundingBox; framingZoom?: number } | null,
    pageNumber: number,
    fileId: string | undefined,
    box: BoundingBox | null
  ): boolean {
    if (!targetA || !box) return false;
    if (targetA.pageNumber !== pageNumber || targetA.fileId !== fileId) return false;
    // The same box asked for closer (Issue #3256) is a new framing, not a repeat.
    if ((targetA.framingZoom ?? null) !== this.highlightFramingZoom()) return false;
    return this.isSameBox(targetA.box, box);
  }

  /**
   * The closer framing for the box on screen (Issue #3256). Only the highlight from the link has
   * one: a region selected in the hierarchy gets the standard framing.
   */
  private highlightFramingZoom(): number | null {
    return this.highlightBox !== null && this.highlightBox === this.urlHighlightBox ? this.routeHighlightZoom : null;
  }

  private isRenderReady(pageIdentity?: PageIdentity): boolean {
    if (this.isLoading || this.isRotating) return false;
    if (!this.pdfCanvas?.nativeElement || !this.pdfContainer?.nativeElement) return false;
    if (this.pdfCanvas.nativeElement.width <= 0) return false;
    if (!this.pdfRenderService.getOriginalPageDimensions()) return false;
    if (pageIdentity) {
      if (!this.renderedPageIdentity) return false;
      if (this.renderedPageIdentity.pageNumber !== pageIdentity.pageNumber) return false;
      if (
        pageIdentity.fileId &&
        this.renderedPageIdentity.fileId &&
        this.renderedPageIdentity.fileId !== pageIdentity.fileId
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * The regions the spotlight draws (Issue #2254).
   *
   * `highlightBox` is shared by three sources: the highlight from the URL or the assistant, the
   * region selected in the hierarchy, and the region under the pointer. Only the first one has
   * individual regions and labels. For the other two this is empty, and the spotlight draws
   * `highlightBox` as the single region it is.
   */
  get spotlightRegions(): readonly HighlightRegion[] {
    return this.highlightBox !== null && this.highlightBox === this.urlHighlightBox
      ? this.urlHighlightRegions
      : NO_HIGHLIGHT_REGIONS;
  }

  private triggerFocusForHighlightBoxIfReady(): void {
    if (!this.highlightBox) return;
    const page = this.page$.getValue();
    if (!page) return;

    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? undefined;
    if (this.isSameHighlightTarget(this.lastFocusedHighlightTarget, page.getPageNumber(), fileId, this.highlightBox)) {
      return;
    }

    const source: FocusSource = this.routeHasHighlight
      ? this.linkHighlightUnits === 'permille'
        ? 'search'
        : 'deepLink'
      : 'assistant';
    const previous = this.lastFocusedHighlightTarget;
    const samePageSource =
      previous &&
      previous.pageNumber === page.getPageNumber() &&
      (!previous.fileId || !fileId || previous.fileId === fileId)
        ? { kind: 'box' as const, box: previous.box }
        : undefined;
    const requestInput: FocusTargetRequest = {
      pageIdentity: {
        pageNumber: page.getPageNumber(),
        fileId,
      },
      geometry: { kind: 'box', box: this.highlightBox },
      highlightKind: 'object',
      source,
      sourceGeometry: samePageSource,
      framingZoom: this.highlightFramingZoom() ?? undefined,
      transitionPolicy: source === 'search' ? 'FULL_PAGE_THEN_FOCUS' : 'AUTO',
      transitionDurationMs: source === 'search' ? 1200 : undefined,
    };

    // Retain canonical service contract: dispatch through previewFocusService so
    // a valid requestId, cancellation token, and completion bookkeeping are created.
    void this.previewFocusService.focusTarget(requestInput);
  }

  private withKnownFocusSource(request: FocusTargetRequest): FocusTargetRequest {
    if (request.sourceGeometry || !this.lastFocusedHighlightTarget) return request;
    const previous = this.lastFocusedHighlightTarget;
    if (
      previous.pageNumber !== request.pageIdentity.pageNumber ||
      (previous.fileId && request.pageIdentity.fileId && previous.fileId !== request.pageIdentity.fileId)
    ) {
      return request;
    }
    return { ...request, sourceGeometry: { kind: 'box', box: previous.box } };
  }

  private async handleFocusTargetRequest(request: FocusTargetRequest): Promise<void> {
    request = this.withKnownFocusSource(request);
    const currentPage = this.page$.getValue();
    const currentPageNum = currentPage?.getPageNumber();
    const currentFileId = currentPage ? (this.extractFileIdFromPagePath(currentPage.getPageFolderPath()) ?? '1') : '1';
    const reqFileId = request.pageIdentity.fileId ?? currentFileId;

    const isCurrentPage =
      currentPage !== null && currentPageNum === request.pageIdentity.pageNumber && currentFileId === reqFileId;

    if (!isCurrentPage) {
      // A page turn establishes a new coordinate system. AUTO starts from whole-page
      // context on the destination instead of pretending the pages share one plane.
      this.pendingFocusTargetRequest = {
        ...request,
        transitionPolicy:
          !request.transitionPolicy || request.transitionPolicy === 'AUTO'
            ? 'FULL_PAGE_THEN_FOCUS'
            : request.transitionPolicy,
      };
      const targetPageNum = request.pageIdentity.pageNumber;
      const currentUrl = this.router.url.split('?')[0];
      const match = currentUrl.match(/^(\/projects\/[^/]+(?:\/files\/[^/]+)?)\/pages\/\d+(.*)$/);
      if (match) {
        const prefix = match[1];
        const suffix = match[2] || '/preview';
        void this.router.navigateByUrl(`${prefix}/pages/${targetPageNum}${suffix}`);
      }
      return;
    }

    if (!this.isRenderReady(request.pageIdentity)) {
      // Current page is still rendering or canvas not ready, stash until render settles
      this.pendingFocusTargetRequest = request;
      return;
    }

    // Current page is already rendered and ready: execute framing
    if (request.geometry.kind === 'box') {
      this.lastFocusedHighlightTarget = {
        pageNumber: request.pageIdentity.pageNumber,
        fileId: request.pageIdentity.fileId,
        box: request.geometry.box,
        framingZoom: request.framingZoom,
      };
    }
    await this.executeFocusFraming(request);
  }

  private handleFocusCancellation(event: FocusCancellationEvent): void {
    const interruptedRunningTransition = this.focusAnimationFrame !== null;
    if (this.focusAnimationFrame !== null) {
      cancelAnimationFrame(this.focusAnimationFrame);
      this.focusAnimationFrame = null;
    }
    if (this.focusAnimationTimeout) {
      clearTimeout(this.focusAnimationTimeout);
      this.focusAnimationTimeout = null;
    }
    const container = this.pdfContainer?.nativeElement;
    if (container) {
      container.dataset['cameraTransitionState'] = 'cancelled';
    }
    if (container && this.focusOriginalScrollBehavior !== null) {
      container.style.scrollBehavior = this.focusOriginalScrollBehavior;
      this.focusOriginalScrollBehavior = null;
    }
    this.pendingFocusTargetRequest = null;
    this.isGestureZooming = false;
    this.focusFlightRunning = false;
    this.endSpotlightFlight();
    const canvas = this.pdfCanvas?.nativeElement;
    if (canvas) {
      canvas.style.transition = 'none';
      if (interruptedRunningTransition) {
        this.adoptDisplayedFocusCamera();
      }
    }
    this.syncSpotlightGestureTransform();
  }

  /**
   * Convert the current compositor-only focus frame into the canvas's layout state.
   * The visible rectangle stays fixed while wheel, pinch, drag, or Escape takes
   * control, and the next gesture starts from the scale the user actually sees.
   */
  private adoptDisplayedFocusCamera(): void {
    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    if (!canvas || !container) return;

    const displayedRect = canvas.getBoundingClientRect();
    const layoutWidth = canvas.clientWidth || Number.parseFloat(canvas.style.width);
    const layoutHeight = canvas.clientHeight || Number.parseFloat(canvas.style.height);
    if (
      displayedRect.width <= 0 ||
      displayedRect.height <= 0 ||
      !Number.isFinite(layoutWidth) ||
      !Number.isFinite(layoutHeight) ||
      layoutWidth <= 0 ||
      layoutHeight <= 0
    ) {
      return;
    }

    // A camera move may run on a layout smaller than the painted scale (Issue #2924).
    const layoutScale = this.focusLayoutScale ?? (this.paintedScale || this.currentScale || 1);
    this.focusLayoutScale = null;
    const adoptedScale = layoutScale * (displayedRect.width / layoutWidth);
    canvas.style.transform = '';
    canvas.style.transformOrigin = '';
    canvas.style.width = `${displayedRect.width}px`;
    canvas.style.height = `${displayedRect.height}px`;

    const normalizedRect = canvas.getBoundingClientRect();
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, container.scrollLeft + normalizedRect.left - displayedRect.left)
    );
    container.scrollTop = Math.max(
      0,
      Math.min(maxScrollTop, container.scrollTop + normalizedRect.top - displayedRect.top)
    );

    const settledRect = canvas.getBoundingClientRect();
    const residualX = displayedRect.left - settledRect.left;
    const residualY = displayedRect.top - settledRect.top;
    this.gestureBaseTransform =
      Math.abs(residualX) > 0.1 || Math.abs(residualY) > 0.1 ? `translate3d(${residualX}px, ${residualY}px, 0)` : '';
    canvas.style.transformOrigin = this.gestureBaseTransform ? '0 0' : '';
    canvas.style.transform = this.gestureBaseTransform;

    this.currentScale = adoptedScale;
    this.paintedScale = adoptedScale;
    this.gestureStartScale = adoptedScale;
    this.gestureCurrentScale = adoptedScale;
    this.gestureRawScale = adoptedScale;
    this.gestureUntransformedRect = settledRect;
    this.syncSpotlightGestureTransform();
    this.updateSpotlightValues();
  }

  drainPendingFocusTargetRequest(generation?: number): void {
    if (generation !== undefined && generation !== this.renderGeneration) {
      return;
    }

    if (!this.pendingFocusTargetRequest) {
      const page = this.page$.getValue();
      if (page && this.highlightBox) {
        const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? undefined;
        if (
          !this.isSameHighlightTarget(this.lastFocusedHighlightTarget, page.getPageNumber(), fileId, this.highlightBox)
        ) {
          this.triggerFocusForHighlightBoxIfReady();
        }
      }
      return;
    }

    const req = this.pendingFocusTargetRequest;
    const currentPage = this.page$.getValue();
    const currentPageNum = currentPage?.getPageNumber();
    const currentFileId = currentPage ? (this.extractFileIdFromPagePath(currentPage.getPageFolderPath()) ?? '1') : '1';
    const reqFileId = req.pageIdentity.fileId ?? currentFileId;

    if (currentPageNum === req.pageIdentity.pageNumber && currentFileId === reqFileId) {
      this.pendingFocusTargetRequest = null;
      if (req.geometry.kind === 'box') {
        this.lastFocusedHighlightTarget = {
          pageNumber: req.pageIdentity.pageNumber,
          fileId: req.pageIdentity.fileId,
          box: req.geometry.box,
          framingZoom: req.framingZoom,
        };
      }
      void this.executeFocusFraming(req);
    }
    // If the active page does not match the pending request, do not cancel or clear
    // the request; keep it pending for the target page render.
  }

  private onPageRendered(renderEvent: {
    page: ArchitecturalPlanPage | null;
    scale: number;
    dims: { width: number; height: number } | null;
  }): void {
    const page = renderEvent.page;
    if (!page) return;

    // Check if this render event is for the current active page; ignore obsolete render events
    const currentPage = this.page$.getValue();
    if (
      !currentPage ||
      currentPage.getPageNumber() !== page.getPageNumber() ||
      currentPage.getPageFolderPath() !== page.getPageFolderPath()
    ) {
      return;
    }

    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath()) ?? undefined;
    this.renderedPageIdentity = {
      pageNumber: page.getPageNumber(),
      fileId,
    };
    if (this.routeHasHighlight && this.linkHighlightUnits === 'permille') {
      markProjectSearchMilestone('firstCanvas');
    }
    if (this.regionChatStore.active || this.regionChatStore.restored) {
      this.updateRegionChatContext(page);
    }

    // If an existing pending focus request is queued (whether a text match, matching-page,
    // or newer target), NEVER overwrite it with an object highlight.
    // Old code deliberately preserved unmatched or existing pending requests.
    if (!this.pendingFocusTargetRequest && this.highlightBox) {
      if (
        !this.isSameHighlightTarget(this.lastFocusedHighlightTarget, page.getPageNumber(), fileId, this.highlightBox)
      ) {
        this.triggerFocusForHighlightBoxIfReady();
      }
    }
  }

  private announceFocus(request: FocusTargetRequest): void {
    const label = request.label || (request.highlightKind === 'text' ? 'text highlight' : 'object highlight');
    this.focusAnnouncement = `Focused on ${label} on page ${request.pageIdentity.pageNumber}`;
    if (request.source === 'search') markProjectSearchMilestone('highlightSettled');
    setTimeout(() => {
      this.focusAnnouncement = '';
    }, 1200);
  }

  private focusViewportGeometry(
    container: HTMLDivElement,
    insets: FocusViewportInsets
  ): { usableWidth: number; usableHeight: number; target: CssPoint } {
    const scrollport = measureScrollport(container);
    const usableWidth = Math.max(100, scrollport.width - insets.side * 2);
    const usableHeight = Math.max(100, scrollport.height - (insets.top + insets.bottom));
    return {
      usableWidth,
      usableHeight,
      target: {
        x: scrollport.left + insets.side + usableWidth / 2,
        y: scrollport.top + insets.top + usableHeight / 2,
      },
    };
  }

  private async executeFocusFraming(request: FocusTargetRequest): Promise<void> {
    if (!this.pdfCanvas?.nativeElement || !this.pdfContainer?.nativeElement) {
      if (request.geometry.kind === 'box') {
        this.lastFocusedHighlightTarget = null;
      }
      this.previewFocusService.notifyFocusCompleted({
        requestId: request.requestId!,
        status: 'skipped',
        reason: 'Canvas or container not available',
      });
      return;
    }

    if (request.framing === 'none') {
      this.previewFocusService.notifyFocusCompleted({
        requestId: request.requestId!,
        status: 'skipped',
      });
      return;
    }

    const canvas = this.pdfCanvas.nativeElement;
    const container = this.pdfContainer.nativeElement;

    // 1. Usable viewport accounting for safe margins and floating find/toolbar
    const focusInsets: FocusViewportInsets = {
      top: this.findOpen ? 72 : 32,
      bottom: 48,
      side: 32,
    };
    const focusViewport = this.focusViewportGeometry(container, focusInsets);
    const usableViewportWidth = focusViewport.usableWidth;
    const usableViewportHeight = focusViewport.usableHeight;

    // 2. Geometry normalization to union bounding box [0, 1]
    const dims = this.pdfRenderService.getOriginalPageDimensions() ?? {
      width: canvas.width > 0 ? canvas.width : 5000,
      height: canvas.height > 0 ? canvas.height : 4000,
    };
    const unionBox = PreviewFocusService.computeUnionBox(request.geometry, dims);

    // 3. Target scale calculation with context padding
    const paddingFraction = Math.max(0.05, Math.min(1.0, request.paddingFraction ?? 0.25));
    // Dimensions at PDF scale 1.0 (HI_DPI_FACTOR = 2, so CSS size = dims / 2)
    const unscaledDisplayWidth = dims.width / 2;
    const unscaledDisplayHeight = dims.height / 2;

    const targetUnscaledWidth = unionBox.width * unscaledDisplayWidth;
    const targetUnscaledHeight = unionBox.height * unscaledDisplayHeight;
    const targetUnscaledCenterX = (unionBox.x + unionBox.width / 2) * unscaledDisplayWidth;
    const targetUnscaledCenterY = (unionBox.y + unionBox.height / 2) * unscaledDisplayHeight;

    const paddedTargetWidth = Math.max(1, targetUnscaledWidth * (1 + 2 * paddingFraction));
    const paddedTargetHeight = Math.max(1, targetUnscaledHeight * (1 + 2 * paddingFraction));

    const fitScaleX = usableViewportWidth / paddedTargetWidth;
    const fitScaleY = usableViewportHeight / paddedTargetHeight;
    // Issue #3256: a closer framing multiplies the fitted scale. The clamp below still applies the
    // maximum zoom, and the target stays centred in the usable (unoccluded) viewport.
    const idealPdfScale = Math.min(fitScaleX, fitScaleY) * focusFramingZoom(request);

    const baseScale = this.pdfRenderService.getBaseScale() || 0.2;
    const maxScale = this.getMaximumZoomScale();

    let targetPdfScale: number;
    if (request.framing === 'centerAtCurrentScale') {
      targetPdfScale = this.pdfRenderService.getCurrentScale();
    } else {
      targetPdfScale = Math.max(baseScale, Math.min(maxScale, idealPdfScale));
    }
    targetPdfScale = this.pdfRenderService.getSafeScale(targetPdfScale);

    // 4. Calculate target scroll positions to center the target in usable viewport
    const finalCanvasWidth = unscaledDisplayWidth * targetPdfScale;
    const finalCanvasHeight = unscaledDisplayHeight * targetPdfScale;

    const targetCenterOnFinalCanvasX = targetUnscaledCenterX * targetPdfScale;
    const targetCenterOnFinalCanvasY = targetUnscaledCenterY * targetPdfScale;

    const targetCanvasPoint = {
      x: targetCenterOnFinalCanvasX,
      y: targetCenterOnFinalCanvasY,
    };

    // 5. Capture the exact displayed camera, including an interrupted compositor transform.
    const displayedCanvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const originalWidth = canvas.style.width;
    const originalHeight = canvas.style.height;
    const originalScrollLeft = container.scrollLeft;
    const originalScrollTop = container.scrollTop;
    this.focusOriginalScrollBehavior = container.style.scrollBehavior;

    const currentPdfScale = this.paintedScale || this.pdfRenderService.getCurrentScale() || 1.0;
    const displayedWidth =
      displayedCanvasRect.width > 0 ? displayedCanvasRect.width : unscaledDisplayWidth * currentPdfScale;
    const displayedHeight =
      displayedCanvasRect.height > 0 ? displayedCanvasRect.height : unscaledDisplayHeight * currentPdfScale;
    const displayedLeft =
      displayedCanvasRect.width > 0 ? displayedCanvasRect.left : containerRect.left - container.scrollLeft;
    const displayedTop =
      displayedCanvasRect.height > 0 ? displayedCanvasRect.top : containerRect.top - container.scrollTop;
    const displayedScaleX = displayedWidth / unscaledDisplayWidth;
    const displayedScaleY = displayedHeight / unscaledDisplayHeight;
    const viewportScreenCenterX = focusViewport.target.x;
    const viewportScreenCenterY = focusViewport.target.y;
    const viewportCenterX = viewportScreenCenterX - containerRect.left;
    const viewportCenterY = viewportScreenCenterY - containerRect.top;
    const sourceIdentity = `${request.pageIdentity.fileId ?? ''}:${request.pageIdentity.pageNumber}`;
    const fromView: CameraView = {
      centerX: (viewportScreenCenterX - displayedLeft) / displayedScaleX,
      centerY: (viewportScreenCenterY - displayedTop) / displayedScaleY,
      visibleWidth: usableViewportWidth / displayedScaleX,
      viewportAspectRatio: usableViewportWidth / usableViewportHeight,
      sourceIdentity,
    };

    // Simulate only the final layout. Animation frames remain compositor-only.
    canvas.style.transition = 'none';
    canvas.style.transform = '';
    canvas.style.transformOrigin = '0 0';
    canvas.style.width = `${finalCanvasWidth}px`;
    canvas.style.height = `${finalCanvasHeight}px`;

    container.style.scrollBehavior = 'auto';
    const focusViewportAtTarget = this.focusViewportGeometry(container, focusInsets);
    // The overlays beside the page still have their old size here. After a zoom out they are wider
    // than the new page and would let the trial scroll run past the sheet edge, so the flight would
    // land there and jump at the commit (Issue #3133). For this one synchronous measurement each
    // overlay is shrunk to nothing and its contents clipped (`contain: strict`), so it adds nothing
    // to the scroll range; nothing is painted in between. `display: none` would do the same but
    // restarts the overlays' CSS animations, which flashed the shading off at every take-off. The
    // browser then applies its own edge stop, and the scroll position is read back, not assumed.
    const overlays = Array.from(container.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement && child !== canvas.parentElement
    );
    const overlayStyles = overlays.map(({ style }) => [style.contain, style.width, style.height] as const);
    for (const { style } of overlays) {
      style.contain = 'strict';
      style.width = '0px';
      style.height = '0px';
    }
    const targetScroll = anchoredScrollPosition(container, canvas, targetCanvasPoint, focusViewportAtTarget.target);
    container.scrollLeft = targetScroll.left;
    container.scrollTop = targetScroll.top;
    const clampedSL = container.scrollLeft;
    const clampedST = container.scrollTop;

    const finalCanvasRect = canvas.getBoundingClientRect();
    const targetView: CameraView = {
      centerX:
        finalCanvasRect.width > 0
          ? (viewportScreenCenterX - finalCanvasRect.left) / (finalCanvasRect.width / unscaledDisplayWidth)
          : finalCanvasWidth <= container.clientWidth
            ? unscaledDisplayWidth / 2
            : (clampedSL + viewportCenterX) / targetPdfScale,
      centerY:
        finalCanvasRect.height > 0
          ? (viewportScreenCenterY - finalCanvasRect.top) / (finalCanvasRect.height / unscaledDisplayHeight)
          : finalCanvasHeight <= container.clientHeight
            ? unscaledDisplayHeight / 2
            : (clampedST + viewportCenterY) / targetPdfScale,
      visibleWidth: usableViewportWidth / targetPdfScale,
      viewportAspectRatio: usableViewportWidth / usableViewportHeight,
      sourceIdentity,
    };

    // Restore the committed layout, but deliberately leave the old transform cleared. The first
    // sampled frame below reconstructs the same displayed view before the browser can paint.
    overlays.forEach(({ style }, index) => ([style.contain, style.width, style.height] = overlayStyles[index]));
    canvas.style.width = originalWidth;
    canvas.style.height = originalHeight;
    container.scrollLeft = originalScrollLeft;
    container.scrollTop = originalScrollTop;

    const targetGeometry: CameraRect = {
      x: unionBox.x * unscaledDisplayWidth,
      y: unionBox.y * unscaledDisplayHeight,
      width: Math.max(Number.EPSILON, unionBox.width * unscaledDisplayWidth),
      height: Math.max(Number.EPSILON, unionBox.height * unscaledDisplayHeight),
    };
    let sourceGeometry: CameraRect | undefined;
    if (request.sourceGeometry) {
      const sourceBox = PreviewFocusService.computeUnionBox(request.sourceGeometry, dims);
      sourceGeometry = {
        x: sourceBox.x * unscaledDisplayWidth,
        y: sourceBox.y * unscaledDisplayHeight,
        width: Math.max(Number.EPSILON, sourceBox.width * unscaledDisplayWidth),
        height: Math.max(Number.EPSILON, sourceBox.height * unscaledDisplayHeight),
      };
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
    const plan = planCameraTransition({
      fromView,
      toView: targetView,
      sourceGeometry,
      targetGeometry,
      // Within one page, an automatic move is one straight flight (Issue #3126). Page changes arrive
      // here already set to FULL_PAGE_THEN_FOCUS, and explicit policies are kept.
      policy:
        !request.transitionPolicy || request.transitionPolicy === 'AUTO' ? 'ARC_FLYOVER' : request.transitionPolicy,
      explicitDurationMs: request.transitionDurationMs,
      pageBounds: { x: 0, y: 0, width: unscaledDisplayWidth, height: unscaledDisplayHeight },
      reducedMotion: prefersReducedMotion,
    });
    container.dataset['cameraTransitionPolicy'] = plan.selectedPolicy;

    if (plan.selectedPolicy === 'INSTANT') {
      canvas.style.transition = 'none';
      this.syncSpotlightGestureTransform();
      const committed = await this.commitFocus(targetPdfScale, clampedSL, clampedST, targetCanvasPoint, focusInsets);
      if (this.focusOriginalScrollBehavior !== null) {
        container.style.scrollBehavior = this.focusOriginalScrollBehavior;
        this.focusOriginalScrollBehavior = null;
      }
      container.dataset['cameraTransitionState'] = 'settled';
      this.announceFocus(request);
      this.previewFocusService.notifyFocusCompleted({
        requestId: request.requestId!,
        status: 'completed',
        targetScale: targetPdfScale,
        scrollLeft: committed.left,
        scrollTop: committed.top,
      });
      return;
    }

    // 6. Sample the path on animation frames. PDF rasterization occurs only in the final commit.
    if (this.focusAnimationFrame !== null) {
      cancelAnimationFrame(this.focusAnimationFrame);
      this.focusAnimationFrame = null;
    }
    if (this.focusAnimationTimeout) {
      clearTimeout(this.focusAnimationTimeout);
      this.focusAnimationTimeout = null;
    }
    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout);
      this.zoomTimeout = null;
    }

    this.cancelResizeRefit();
    this.pdfRenderService.cancelCurrentRender();
    this.isGestureZooming = true;
    container.dataset['cameraTransitionState'] = 'running';
    canvas.style.transition = 'none';

    // Lay the canvas and its overlays out at the widest view of the move (Issue #2924). Every
    // frame then enlarges a layer about the size of the viewport. Shrinking the starting layout
    // instead would scale down overlays that, deep in a zoom, are thousands of pixels across, and
    // the browser shows the parts of them it never drew as holes in the shading.
    const startLayoutScale = (canvas.clientWidth || unscaledDisplayWidth * currentPdfScale) / unscaledDisplayWidth;
    const animationLayoutScale = widestCameraScale(plan, usableViewportWidth);
    let baseLayoutScale = currentPdfScale;
    if (animationLayoutScale < startLayoutScale * FOCUS_RELAYOUT_SCALE_RATIO) {
      baseLayoutScale = animationLayoutScale;
      this.focusLayoutScale = animationLayoutScale;
      canvas.style.width = `${unscaledDisplayWidth * animationLayoutScale}px`;
      canvas.style.height = `${unscaledDisplayHeight * animationLayoutScale}px`;
      // The overlays measure the canvas only while no transform is active on it.
      canvas.style.transformOrigin = '';
      this.syncSpotlightGestureTransform();
      this.updateSpotlightValues();
    }
    const baseCanvasRect = canvas.getBoundingClientRect();
    const baseCanvasWidth = baseCanvasRect.width > 0 ? baseCanvasRect.width : unscaledDisplayWidth * baseLayoutScale;
    const baseCanvasHeight =
      baseCanvasRect.height > 0 ? baseCanvasRect.height : unscaledDisplayHeight * baseLayoutScale;
    const baseCanvasLeft = baseCanvasRect.width > 0 ? baseCanvasRect.left : containerRect.left - container.scrollLeft;
    const baseCanvasTop = baseCanvasRect.height > 0 ? baseCanvasRect.top : containerRect.top - container.scrollTop;

    canvas.style.transformOrigin = '0 0';

    const applyView = (view: CameraView): void => {
      const desiredPageScale = usableViewportWidth / view.visibleWidth;
      const desiredCanvasWidth = unscaledDisplayWidth * desiredPageScale;
      const desiredCanvasHeight = unscaledDisplayHeight * desiredPageScale;
      const scaleX = desiredCanvasWidth / baseCanvasWidth;
      const scaleY = desiredCanvasHeight / baseCanvasHeight;
      const desiredLeft = viewportScreenCenterX - view.centerX * desiredPageScale;
      const desiredTop = viewportScreenCenterY - view.centerY * desiredPageScale;
      const translateX = desiredLeft - baseCanvasLeft;
      const translateY = desiredTop - baseCanvasTop;
      canvas.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale3d(${scaleX}, ${scaleY}, 1)`;
      this.syncSpotlightGestureTransform();
      this.paintSpotlightFlight(desiredLeft, desiredTop, desiredPageScale / 2, desiredCanvasWidth, desiredCanvasHeight);
    };

    this.endSpotlightFlight();
    this.focusFlightRunning = true;
    applyView(sampleCameraTransition(plan, 0));
    let startedAt: number | null = null;
    const finish = async (): Promise<void> => {
      this.focusAnimationFrame = null;
      this.focusLayoutScale = null;
      this.isGestureZooming = false;
      this.focusFlightRunning = false;
      const committed = await this.commitFocus(targetPdfScale, clampedSL, clampedST, targetCanvasPoint, focusInsets);
      // The SVG shading has its real geometry again; let it take over from the flight canvas.
      this.endSpotlightFlight();
      if (this.focusOriginalScrollBehavior !== null) {
        container.style.scrollBehavior = this.focusOriginalScrollBehavior;
        this.focusOriginalScrollBehavior = null;
      }
      container.dataset['cameraTransitionState'] = 'settled';
      this.announceFocus(request);
      this.previewFocusService.notifyFocusCompleted({
        requestId: request.requestId!,
        status: 'completed',
        targetScale: targetPdfScale,
        scrollLeft: committed.left,
        scrollTop: committed.top,
      });
    };
    const animate = (now: number): void => {
      startedAt ??= now;
      const elapsed = Math.min(plan.durationMs, Math.max(0, now - startedAt));
      applyView(sampleCameraTransition(plan, elapsed));
      if (elapsed >= plan.durationMs) {
        void finish();
        return;
      }
      this.focusAnimationFrame = requestAnimationFrame(animate);
    };
    this.focusAnimationFrame = requestAnimationFrame(animate);
  }

  private async commitFocus(
    targetScale: number,
    scrollLeft: number,
    scrollTop: number,
    targetCanvasPoint: CssPoint,
    focusInsets: FocusViewportInsets
  ): Promise<ScrollPosition> {
    if (!this.pdfCanvas?.nativeElement || !this.pdfContainer?.nativeElement) {
      return { left: scrollLeft, top: scrollTop };
    }
    const canvas = this.pdfCanvas.nativeElement;
    const container = this.pdfContainer.nativeElement;
    let committedScroll = { left: scrollLeft, top: scrollTop };

    await this.pdfRenderService.setZoomAndScroll(targetScale, scrollLeft, scrollTop, () => {
      canvas.style.transition = 'none';
      canvas.style.transform = '';
      canvas.style.transformOrigin = '';

      // Resolve against the current drawer/chat layout after the target canvas
      // size and both scrollbar axes are known, but before the new bitmap paints.
      const focusViewport = this.focusViewportGeometry(container, focusInsets);
      committedScroll = anchoredScrollPosition(container, canvas, targetCanvasPoint, focusViewport.target);
      container.scrollLeft = committedScroll.left;
      container.scrollTop = committedScroll.top;

      this.syncSpotlightGestureTransform();
      this.updateSpotlightValues();
    });

    this.updateScaleValues();
    return committedScroll;
  }

  private async openInNewTab(): Promise<void> {
    const pdfUrl = await this.pdfRenderService.getPdfUrl();
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  }

  private async rotatePage(degrees: number): Promise<void> {
    const page = this.page$.getValue();
    if (!page || this.isRotating) return;

    const projectId = page.getArchitecturalPlanId();
    const pageNumber = page.getPageNumber();
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath());

    // Cancel in-flight camera animation before rotating
    if (this.focusAnimationTimeout) {
      clearTimeout(this.focusAnimationTimeout);
      this.focusAnimationTimeout = null;
    }
    this.isGestureZooming = false;
    this.previewFocusService.cancelCurrentFocus('Page rotation');

    // Issue #2445: tell the server which rotation this view shows. A request that is repeated after
    // a lost response is then answered without turning the page again, and a request made against
    // a view that is out of date is refused instead of turning the page somewhere unexpected.
    const rotationBefore = normalizeRotation(this.pdfRenderService.getPageRotation());

    this.isRotating = true;
    try {
      const result = await this.pageRotationService.rotatePage(
        projectId,
        pageNumber,
        degrees,
        fileId || undefined,
        rotationBefore
      );

      if (result.success) {
        await this.showPageAfterRotation(page);
        this.snackBar.open(result.message, 'OK', { duration: 3000 });
      } else {
        this.snackBar.open('Rotation failed: ' + result.message, 'Close', { duration: 5000 });
      }
    } catch (error: any) {
      ClientLoggingService.log('ERROR', '[Preview] Rotation failed:', error);
      if (error instanceof PageRotationConflictError) {
        // Nothing was changed on the server. The page shown here may be out of date, so show it
        // as it is stored now and let the reviewer decide whether a turn is still needed.
        await this.showPageAfterRotation(page).catch(() => undefined);
        this.snackBar.open(
          'This page was being rotated elsewhere. It has been reloaded; rotate again if still needed.',
          'Close',
          { duration: 6000 }
        );
      } else {
        this.snackBar.open('Failed to rotate page', 'Close', { duration: 5000 });
      }
    } finally {
      this.isRotating = false;
    }
  }

  /**
   * Shows the page as it is stored after a rotation request, and moves everything drawn on it to
   * follow (Issue #2445).
   *
   * The server keeps region boxes, OCR tokens and the page consistent, but this view also holds
   * positions of its own that were computed for the old rotation: the active highlight, a highlight
   * from the link, an in-progress measurement and a text match. Each is brought to the rotation the
   * page is actually rendered at, which is read back from the PDF after the render rather than
   * assumed from the request.
   */
  private async showPageAfterRotation(page: ArchitecturalPlanPage): Promise<void> {
    const projectId = page.getArchitecturalPlanId();
    const fileId = this.extractFileIdFromPagePath(page.getPageFolderPath());

    // The text match on screen has to be read before the caches are invalidated: invalidating them
    // resets the text search, which clears the match this view is holding.
    const selector = this.pendingTextMatch?.selector;

    // Invalidate both L1 (byte) and L2 (parsed pdf.js) caches
    this.planReviewerService.invalidatePagePdfCache(page);
    this.pdfRenderService.invalidatePageCache(page);

    // Reset lastFocusedHighlightTarget and renderedPageIdentity
    // so that active highlights or new requests re-frame to the new rotation geometry
    this.lastFocusedHighlightTarget = null;
    this.renderedPageIdentity = null;

    // Re-render the page with fresh data from backend
    await this.pdfRenderService.renderPdfForPage(page);
    // updateScaleValues recomputes every highlight for the rotation now rendered. It also hands the
    // rendered rotation to the scale controller, whose `rotation` is bound to the measure overlay's
    // `pageRotation` input; the overlay turns the ruler and drawn areas from that single change
    // (Issue #2542), so nothing here turns them a second time.
    this.updateScaleValues();
    this.paintedScale = this.currentScale;

    this.rememberLinkHighlightRotation();

    // Rotation replaces the underlying PDF, so recompute digital-text
    // coordinates in the new viewport before repainting the match.
    if (selector) {
      await this.textFocus.focusSelector(
        projectId,
        fileId ?? '1',
        page,
        selector,
        () => this.page$.getValue() === page
      );
    }
  }

  /**
   * Writes the rotation a link's highlight was measured at into the address bar (Issue #2445).
   *
   * A link written before rotations were tracked carries boxes with no rotation. While the page is
   * open this view knows which rotation they belong to, because it saw the page before it was
   * turned. A reload would lose that and place the boxes on the turned page as they are. Adding the
   * rotation to the link keeps the highlight on the same content after a reload and when the link
   * is copied.
   */
  private rememberLinkHighlightRotation(): void {
    if (!this.linkHighlightRegions.length || this.linkHighlightRotation === null) return;
    if (!this.linkHighlightRotationAssumed) return;
    this.linkHighlightRotationAssumed = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { highlightRotation: String(this.linkHighlightRotation) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private extractFileIdFromPagePath(pageFolderPath: string): string | null {
    if (!pageFolderPath) return null;
    const match = pageFolderPath.match(/\/files\/([^/]+)(?:\/|$)/);
    return match ? match[1] : null;
  }

  /**
   * Where a region of the hierarchy sits on the page as rendered now, in PDF points.
   *
   * The server states which page rotation each box is expressed for (Issue #2445). When the page is
   * rendered at another rotation, for example straight after it was turned and before the hierarchy
   * is fetched again, the box is turned by the difference so it stays on the same content.
   */
  private regionToBoundingBox(region: RegionHierarchyTreeNode.AsObject | null): BoundingBox | null {
    if (!region?.boundingBoxList || region.boundingBoxList.length !== 4) return null;
    const dims = this.pdfRenderService.getOriginalPageDimensions();
    if (!dims) return null;
    return permilleYxBoxToDisplayRect(
      region.boundingBoxList,
      region.boundingBoxRotationDegrees,
      normalizeRotation(this.pdfRenderService.getPageRotation()),
      dims
    );
  }

  private toPageRotation(degrees: number | null): PageRotation | null {
    return degrees === null ? null : normalizeRotation(degrees);
  }

  /**
   * Recomputes the highlight for the rotation the page is rendered at now (Issue #2445).
   *
   * The highlight has three possible sources, in order of precedence: the region under the pointer,
   * the region selected in the hierarchy, and the regions named by the link. Each is kept in the
   * form it arrived in, together with the rotation it was expressed for, and converted here. Before
   * this, the highlight was converted once when it arrived and kept as pixels, so it stayed where
   * it was when the page turned underneath it.
   *
   * Called whenever a source changes and after every render.
   */
  private refreshHighlightGeometry(): void {
    this.highlightGeometryKey = this.renderedGeometryKey();
    const renderedDims = this.pdfRenderService.getOriginalPageDimensions();
    this.renderedPageRotation = renderedDims ? normalizeRotation(this.pdfRenderService.getPageRotation()) : null;
    this.renderedPageSize = renderedDims ? `${renderedDims.width}x${renderedDims.height}` : null;
    this.refreshLinkHighlight();
    const hoveredBox = this.regionToBoundingBox(this.hoveredRegion);
    const activeBox = this.regionToBoundingBox(this.tabService.getActiveRegion());
    const regionBox = hoveredBox ?? activeBox;
    const derived = regionBox ?? this.urlHighlightBox;
    // Hovering another row keeps the selected region in view, half shaded (Issue #3127).
    this.dimmedHighlightBox = hoveredBox && activeBox && !this.isSameBox(hoveredBox, activeBox) ? activeBox : null;
    // Replace the highlight when a source provides one, and clear it only if this method put it
    // there. A highlight set by other means is left alone when no source has anything to say, so a
    // repaint cannot remove it.
    if (derived !== null || this.highlightBox === this.derivedHighlightBox) {
      this.highlightBox = derived;
    }
    this.derivedHighlightBox = derived;
  }

  /** Identifies the rotation and size the page is rendered at; `null` before the first render. */
  private renderedGeometryKey(): string | null {
    const dims = this.pdfRenderService.getOriginalPageDimensions();
    return dims ? `${normalizeRotation(this.pdfRenderService.getPageRotation())}:${dims.width}x${dims.height}` : null;
  }

  /**
   * Decides which rotation a link's boxes belong to when the link does not say (Issue #2445).
   *
   * Such a link is read as it always was: as matching the page the first time it is shown. That can
   * only be decided once the page the link points at is on screen, so nothing is assumed while
   * another page, or no page, is rendered.
   */
  private assumeLinkHighlightRotation(): void {
    if (this.linkHighlightRotation !== null || !this.linkHighlightRegions.length) return;
    if (!this.renderedPageIdentity || !this.pdfRenderService.getOriginalPageDimensions()) return;
    this.linkHighlightRotation = normalizeRotation(this.pdfRenderService.getPageRotation());
    this.linkHighlightRotationAssumed = true;
  }

  /** Derives the link's highlight for the rendered rotation. */
  private refreshLinkHighlight(): void {
    const regions = this.linkHighlightRegions;
    if (!regions.length) {
      this.urlHighlightRegions = NO_HIGHLIGHT_REGIONS;
      this.urlHighlightBox = null;
      return;
    }
    this.assumeLinkHighlightRotation();
    const dims = this.pdfRenderService.getOriginalPageDimensions();
    const rendered = dims ? normalizeRotation(this.pdfRenderService.getPageRotation()) : null;
    const linkRotation = this.linkHighlightRotation;
    if (this.linkHighlightUnits === 'permille') {
      if (!dims || rendered === null || linkRotation === null) {
        this.urlHighlightRegions = NO_HIGHLIGHT_REGIONS;
        this.urlHighlightBox = null;
        return;
      }
      const displayed = regions.flatMap(region => {
        const box = region.box;
        const valid =
          box.x >= 0 &&
          box.y >= 0 &&
          box.width > 0 &&
          box.height > 0 &&
          box.x + box.width <= 1000 &&
          box.y + box.height <= 1000;
        if (!valid) return [];
        const converted = permilleYxBoxToDisplayRect(
          [box.y, box.x, box.y + box.height, box.x + box.width],
          linkRotation,
          rendered,
          dims
        );
        const left = Math.max(0, converted.x - HIGHLIGHT_PADDING_POINTS);
        const top = Math.max(0, converted.y - HIGHLIGHT_PADDING_POINTS);
        const right = Math.min(dims.width, converted.x + converted.width + HIGHLIGHT_PADDING_POINTS);
        const bottom = Math.min(dims.height, converted.y + converted.height + HIGHLIGHT_PADDING_POINTS);
        return [{ ...region, box: { x: left, y: top, width: right - left, height: bottom - top } }];
      });
      // Keep the same objects while only a render/resize notification repeated the same geometry.
      // The spotlight uses identity to decide whether a newly selected target should animate.
      if (!this.sameRegions(displayed, this.urlHighlightRegions)) {
        this.urlHighlightRegions = displayed;
        this.urlHighlightBox =
          displayed.length === 1 ? displayed[0].box : unionBox(displayed.map(region => region.box));
      }
      return;
    }
    if (!dims || rendered === null || linkRotation === null || linkRotation === rendered) {
      // Nothing to turn: show the link's own objects, so the box is exactly the one announced.
      this.urlHighlightRegions = regions;
      this.urlHighlightBox = this.linkHighlightBox ?? unionBox(regions.map(region => region.box));
      return;
    }
    const displayed = regions.map(region => ({
      ...region,
      box: displayRectToRotation(region.box, linkRotation, rendered, dims),
    }));
    // Keep the same objects while nothing changed: the spotlight compares by identity to decide
    // whether the highlight on screen is the link's.
    if (!this.sameRegions(displayed, this.urlHighlightRegions)) {
      this.urlHighlightRegions = displayed;
      this.urlHighlightBox = displayed.length === 1 ? displayed[0].box : unionBox(displayed.map(region => region.box));
    }
  }

  private sameRegions(first: readonly HighlightRegion[], second: readonly HighlightRegion[]): boolean {
    return (
      first.length === second.length &&
      first.every((region, index) => {
        const other = second[index];
        return (
          region.label === other.label &&
          region.box.x === other.box.x &&
          region.box.y === other.box.y &&
          region.box.width === other.box.width &&
          region.box.height === other.box.height
        );
      })
    );
  }

  private updateScaleValues(): void {
    this.currentScale = this.pdfRenderService.getCurrentScale();
    const baseScale = this.pdfRenderService.getBaseScale();
    // A new fitted scale means another page or a resized drawing area, so an earlier Fit to width
    // or height no longer describes the page.
    if (Math.abs(baseScale - this.baseScale) > 0.0001) this.fittedTo = null;
    this.baseScale = baseScale;

    // Sync gesture state variables to match the actual underlying canvas
    if (!this.isGestureZooming) {
      this.paintedScale = this.currentScale;
      this.gestureCurrentScale = this.currentScale;
      this.gestureRawScale = this.currentScale;
    }

    // Issue #2442: measurements are computed from the page's size in points and its rotation.
    this.scale.setGeometry(
      this.pdfRenderService.getUnrotatedPageSize?.() ?? null,
      normalizeRotation(this.pdfRenderService.getPageRotation?.())
    );

    // Issue #2445: the page may now be rendered at another rotation than the highlight was last
    // computed for.
    if (this.renderedGeometryKey() !== this.highlightGeometryKey) {
      this.refreshHighlightGeometry();
    } else {
      this.assumeLinkHighlightRotation();
    }

    // Update spotlight values to trigger change detection
    this.updateSpotlightValues();
  }

  /**
   * Update spotlight overlay values.
   * Called after zoom/render to trigger change detection in child component.
   */
  private updateSpotlightValues(): void {
    // Pending render promises and zoom timers can land after the view is torn
    // down; bail out rather than measuring (or change-detecting) a dead view.
    if (!this.pdfCanvas?.nativeElement) return;

    // While an inline transform is driving the canvas (pinch/trackpad gesture,
    // smooth-zoom animation or boundary bounce), getBoundingClientRect() reports
    // the *transformed* box while clientWidth/Height report the untransformed one.
    // Recomputing from that mix would bake the gesture into the cached offsets and
    // then mirror it again — a double-applied zoom. The stencil is instead kept in
    // lockstep by syncSpotlightGestureTransform() for the duration of the gesture,
    // and exact geometry is recomputed the moment the canvas repaints at the new
    // scale (see commitGestureZoom). Issue #1489.
    if (this.isCanvasTransformActive()) {
      this.syncSpotlightGestureTransform();
      return;
    }

    this.spotlightWrapperWidth = this.getWrapperWidth();
    this.spotlightWrapperHeight = this.getWrapperHeight();
    this.spotlightCanvasWidth = this.getCanvasWidth();
    this.spotlightCanvasHeight = this.getCanvasHeight();
    this.spotlightCanvasScale = this.getCanvasScale();
    this.spotlightCanvasOffsetX = this.getCanvasOffsetX();
    this.spotlightCanvasOffsetY = this.getCanvasOffsetY();
    this.overlayLayoutOrigin = this.getCanvasLayoutOffset();

    // Keep these cached values current before the first chat highlight. The
    // ResizeObserver must not be guarded by highlightBox: that was #1408's
    // stale-offset root cause.
    const hasHighlight = !!this.highlightBox || this.textHighlightBoxes.length > 0;
    // The region surface and the measure overlay are bound to the same values, and nothing hides
    // stale ones there: after a zoom the swap clears their mirrored transform straight in the DOM,
    // and it can run outside Angular's zone. Push the new geometry to them now as well, or the
    // outline stays at its pre-zoom place until the next pointer move (Issue #1747).
    const hasGeometryOverlay = !!this.regionChatSurface || !!this.measureOverlay;
    if (!hasHighlight && !hasGeometryOverlay) return;

    // Force change detection to propagate to child components
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    if (hasHighlight) this.logSpotlightDiagnostics();
  }

  /**
   * True while the canvas's painted box can disagree with its layout box — i.e.
   * during a pinch/trackpad gesture, a smooth-zoom transition, or a boundary
   * bounce. Geometry must not be re-measured in that window.
   *
   * Checking `transform` alone is not enough: an animation back to identity
   * clears the transform *and then lets it transition*, so the painted box keeps
   * moving for the duration while `style.transform` already reads empty
   * (triggerBounce phase 2 does exactly this). Re-measuring there would mix the
   * animating getBoundingClientRect() with the untransformed clientWidth — the
   * very drift this issue is about, during the bounce-back the acceptance
   * criteria call out.
   *
   * A live transition or a leftover transform-origin therefore also count as
   * active. Both are cleared on every settle path, and `transition` on this
   * canvas is only ever used for `transform`.
   */
  private isCanvasTransformActive(): boolean {
    const style = this.pdfCanvas?.nativeElement?.style;
    if (!style) return false;

    const transform = style.transform;
    if (transform && transform !== 'none') return true;

    const transition = style.transition;
    if (transition && transition !== 'none') return true;

    return !!style.transformOrigin;
  }

  /**
   * Mirror the canvas's live CSS transform onto the spotlight stencil so the
   * highlight follows pinch/trackpad zoom frame-for-frame instead of freezing at
   * its pre-gesture geometry and snapping when the gesture settles (Issue #1489).
   *
   * The stencil's SVG occupies exactly the canvas's box (the overlay div is placed
   * at the canvas offset and sized to the canvas), so the canvas's transform and
   * canvas-local transform-origin can be replayed verbatim. Copying the transition
   * too means the overshoot/bounce-back animations are shared rather than
   * re-implemented, which keeps the two provably in step.
   *
   * Writes go straight to the DOM: this runs on every gesture frame and must not
   * enter Angular's change-detection path.
   */
  /**
   * Draw the spotlight shading for one flight frame (Issue #3127). `pageLeft`/`pageTop` are where
   * the page's top-left corner is on screen and `displayScale` turns page-pixel boxes into screen
   * pixels. The painter starts on the first frame that has a highlight, so a highlight that appears
   * during the flight is painted too.
   */
  private paintSpotlightFlight(
    pageLeft: number,
    pageTop: number,
    displayScale: number,
    pageWidth: number,
    pageHeight: number
  ): void {
    const overlay = this.spotlightOverlay;
    if (!this.focusFlightRunning || !overlay || !this.highlightBox) return;
    const flight = this.spotlightFlight ?? this.startSpotlightFlight(overlay);
    if (!flight) return;
    const elapsed = performance.now() - flight.startedAt;
    const windows: FlightWindow[] = overlay.cutouts.map(cutout => ({
      x: pageLeft - flight.origin.x + cutout.pageBox.x * displayScale,
      y: pageTop - flight.origin.y + cutout.pageBox.y * displayScale,
      width: cutout.pageBox.width * displayScale,
      height: cutout.pageBox.height * displayScale,
      clearness: flightWindowClearness(cutout, elapsed),
      outlined: overlay.annotated && cutout.state === 'shown',
    }));
    const page = { x: pageLeft - flight.origin.x, y: pageTop - flight.origin.y, width: pageWidth, height: pageHeight };
    flight.painter.paint(windows, page, overlay.overlayOpacity, this.spotlightFeatherPct);
  }

  /**
   * Put a canvas over the scrollport for the flight and hide the SVG shading. The canvas exists only
   * while a flight runs, so outside flights the page has its one canvas as before.
   */
  private startSpotlightFlight(overlay: SpotlightOverlayComponent): typeof this.spotlightFlight {
    const container = this.pdfContainer?.nativeElement;
    const parent = container?.parentElement;
    if (!container || !parent) return null;
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.dataset['testid'] = 'preview-spotlight-flight';
    // Above the page and its overlays, below the toolbars, and transparent to the pointer.
    Object.assign(canvas.style, { position: 'absolute', zIndex: '1', pointerEvents: 'none' });
    parent.insertBefore(canvas, container.nextSibling);
    // The canvas is positioned inside the preview container; the windows are drawn relative to the
    // scrollport's top-left corner.
    const scrollport = measureScrollport(container);
    const parentRect = parent.getBoundingClientRect();
    const painter = new SpotlightFlightPainter(canvas);
    painter.resize(
      scrollport.left - parentRect.left,
      scrollport.top - parentRect.top,
      scrollport.width,
      scrollport.height,
      window.devicePixelRatio || 1
    );
    overlay.setFlightMode(true);
    this.spotlightFlight = {
      canvas,
      painter,
      startedAt: performance.now(),
      origin: { x: scrollport.left, y: scrollport.top },
    };
    return this.spotlightFlight;
  }

  /** Remove the flight canvas and show the SVG shading again. */
  private endSpotlightFlight(): void {
    const flight = this.spotlightFlight;
    if (!flight) return;
    this.spotlightFlight = null;
    flight.canvas.remove();
    this.spotlightOverlay?.setFlightMode(false);
  }

  private syncSpotlightGestureTransform(): void {
    const canvas = this.pdfCanvas?.nativeElement;
    if (!canvas) return;

    const transform = this.overlayGestureTransform(canvas);
    this.spotlightOverlay?.setGestureTransform(transform, canvas.style.transformOrigin, canvas.style.transition);
    this.textHighlightOverlay?.setGestureTransform(transform, canvas.style.transformOrigin, canvas.style.transition);
    this.measureOverlay?.setGestureTransform(transform, canvas.style.transformOrigin, canvas.style.transition);
    this.textSelectionOverlay?.setGestureTransform(transform, canvas.style.transformOrigin, canvas.style.transition);
    this.regionChatSurface?.setGestureTransform(transform, canvas.style.transformOrigin, canvas.style.transition);
  }

  /** The canvas's layout position, before any transform, when the overlays' offsets were last measured. */
  private overlayLayoutOrigin: { x: number; y: number } | null = null;

  /**
   * The transform the overlays need to draw where the canvas is drawn.
   *
   * The overlays sit at the canvas offset measured last time the page settled, and the canvas's
   * transform applies to the canvas where it is laid out now. During a zoom gesture those agree.
   * While the drawing area is being resized they do not: a drawer opening or closing moves the
   * drawing area's edge every frame, the centred canvas moves with it by half that distance, and
   * the overlays stayed where they were, so the shading slid off the page by up to a hundred
   * pixels and snapped back at the end (Issue #3243). The overlays are moved by the canvas's own
   * layout change before the canvas's transform is applied, with no re-measuring of their inputs.
   */
  private overlayGestureTransform(canvas: HTMLCanvasElement): string {
    const transform = canvas.style.transform;
    const origin = this.overlayLayoutOrigin;
    if (!this.resizePreviewActive || !origin || !transform) return transform;
    const now = this.getCanvasLayoutOffset();
    if (!now) return transform;
    const dx = now.x - origin.x;
    const dy = now.y - origin.y;
    if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) return transform;
    return `translate3d(${dx}px, ${dy}px, 0) ${transform}`;
  }

  /**
   * The canvas's untransformed position inside the drawing area (the PDF container), which is
   * where the overlays are laid out. offsetLeft and offsetTop ignore transforms, so this reads
   * the layout even while a transform is drawn. Both elements are measured up the whole
   * offsetParent chain and subtracted, so the result does not depend on which elements in
   * between are positioned, and a move of the container itself (which carries the overlays
   * along with it) cancels out.
   */
  private getCanvasLayoutOffset(): { x: number; y: number } | null {
    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    if (!canvas || !container) return null;
    const c = PreviewComponent.layoutOffsetFromRoot(canvas);
    const o = PreviewComponent.layoutOffsetFromRoot(container);
    return { x: c.x - o.x, y: c.y - o.y };
  }

  private static layoutOffsetFromRoot(element: HTMLElement): { x: number; y: number } {
    let x = 0;
    let y = 0;
    for (let el: HTMLElement | null = element; el; el = el.offsetParent as HTMLElement | null) {
      x += el.offsetLeft;
      y += el.offsetTop;
    }
    return { x, y };
  }

  /**
   * Emits live layout evidence for power-user investigations without adding
   * noise for standard users. The signature check logs only when the rendered
   * highlight geometry changes (for example, placeholder canvas -> PDF page).
   */
  private logSpotlightDiagnostics(): void {
    if (!this.highlightBox || !this.tabService.isPowerUserModeEnabled()) return;

    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    const overlay = container?.querySelector<HTMLElement>('.spotlight-overlay');
    if (!canvas || !container) return;

    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const overlayRect = overlay?.getBoundingClientRect();
    const rectValues = (rect: DOMRect) => ({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
    const scaledBox = {
      x: this.highlightBox.x * this.spotlightCanvasScale,
      y: this.highlightBox.y * this.spotlightCanvasScale,
      width: this.highlightBox.width * this.spotlightCanvasScale,
      height: this.highlightBox.height * this.spotlightCanvasScale,
    };
    const details = {
      highlightBox: this.highlightBox,
      scaledBox,
      featherPct: this.spotlightFeatherPct,
      featherPx: resolveFeatherPx(this.spotlightFeatherPct, scaledBox.width, scaledBox.height),
      devicePixelRatio: window.devicePixelRatio,
      canvas: {
        rect: rectValues(canvasRect),
        bitmapWidth: canvas.width,
        bitmapHeight: canvas.height,
        bitmapToCssScaleX: canvas.clientWidth ? canvas.width / canvas.clientWidth : null,
        bitmapToCssScaleY: canvas.clientHeight ? canvas.height / canvas.clientHeight : null,
      },
      container: { rect: rectValues(containerRect), scrollLeft: container.scrollLeft, scrollTop: container.scrollTop },
      overlay:
        overlay && overlayRect
          ? { rect: rectValues(overlayRect), cssLeft: overlay.style.left, cssTop: overlay.style.top }
          : null,
      spotlightCanvasScale: this.spotlightCanvasScale,
      spotlightCanvasOffsetX: this.spotlightCanvasOffsetX,
      spotlightCanvasOffsetY: this.spotlightCanvasOffsetY,
    };
    const signature = JSON.stringify(details);
    if (signature === this.spotlightDiagnosticSignature) return;

    this.spotlightDiagnosticSignature = signature;
    console.log('[Preview] Spotlight geometry diagnostics', details);
  }

  /**
   * Watch for container resizes (chat panel docked/undocked, TOC drawer toggled)
   * and recalculate spotlight overlay position. Without this, the highlight box
   * drifts when the available viewport width changes.
   */
  private setupResizeObserver(): void {
    const container = this.pdfContainer?.nativeElement;
    const canvas = this.pdfCanvas?.nativeElement;
    const wrapper = this.canvasWrapper?.nativeElement;
    if (!container) return;

    container.addEventListener('scroll', this.onPdfContainerScroll, { passive: true });

    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(entries => {
      this.updateSpotlightValues();
      // Keeping a chosen fit (Issue #2542) follows the drawing area alone. The canvas and its wrapper
      // are observed too, and every zoom resizes those.
      const area = entries.find(entry => entry.target === container)?.contentRect;
      if (area) this.onDrawingAreaResized(area.width, area.height);
    });

    // The container itself does not resize when a non-docked chat drawer is
    // shown.  A fresh Preview route initially has a placeholder-sized canvas,
    // then PdfRenderService replaces it with the rendered page.  Observe that
    // geometry change directly so the overlay is positioned after the canvas
    // has its final layout, including its centering/margin offset.
    this.resizeObserver.observe(container);
    if (wrapper) this.resizeObserver.observe(wrapper);
    if (canvas) this.resizeObserver.observe(canvas);
  }

  private drawingAreaSize: { width: number; height: number } | null = null;
  private stickyFitTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Keeps a chosen fit when the drawing area changes size (requester's feedback on #2542): opening
   * or closing a drawer, docking chat, or resizing the window fits the page again the same way.
   *
   * The fit is "chosen" for as long as the page is still at the zoom that fit produced, which is
   * what the Zoom menu shows as checked. Once the reviewer zooms by hand there is no fit to keep,
   * and their zoom is left alone.
   *
   * Rendering the page takes far longer than a frame, so the page follows the size on every frame
   * with a transform of what is already painted, and the sharp re-render waits for the size to
   * settle (Issue #2920). Before that change the page kept its old size for the whole resize and
   * then jumped.
   */
  onDrawingAreaResized(width: number, height: number): void {
    const previous = this.drawingAreaSize;
    this.drawingAreaSize = { width, height };
    if (!previous || (Math.abs(previous.width - width) < 1 && Math.abs(previous.height - height) < 1)) return;
    const mode = this.fitMode;
    if (!mode || this.isLoading || this.isGestureZooming) return;
    // A re-render started for an earlier size would swap in a page of the wrong size mid-resize.
    if (this.refitInFlight) this.pdfRenderService.cancelCurrentRender();
    // A picture still fading from the last re-fit stays where it was while the page moves (Issue #3245).
    this.pdfRenderService.cancelSwapFade();
    this.refitGeneration++;
    this.previewRefit(mode);
    if (this.stickyFitTimer) clearTimeout(this.stickyFitTimer);
    this.stickyFitTimer = setTimeout(() => {
      this.stickyFitTimer = null;
      void this.refit(mode);
    }, PreviewComponent.STICKY_FIT_SETTLE_MS);
  }

  /** How long the drawing area's size must hold still before a chosen fit is rendered again. */
  private static readonly STICKY_FIT_SETTLE_MS = 150;

  /** Whether the canvas is drawn through a resize preview transform (Issue #2920). */
  private resizePreviewActive = false;
  /** Counts drawing-area changes, so a re-fit overtaken by a newer size leaves its preview alone. */
  private refitGeneration = 0;
  private refitInFlight = false;

  /**
   * Draws the page, on this frame, where the chosen fit will put it at the drawing area's current
   * size: the painted canvas is scaled and moved with a transform, and the overlays are given the
   * same transform so highlights stay on the drawing (Issue #2920).
   */
  private previewRefit(mode: PreviewFitMode): void {
    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    const scale = this.pdfRenderService.getFitScale(mode);
    const target = scale === null ? null : this.pdfRenderService.getDisplaySize(scale);
    if (!canvas || !container || !target || !canvas.offsetWidth || !canvas.offsetHeight) return;

    const [x, y] = this.resizePreviewAxes(canvas, container, target);
    canvas.style.transition = 'none';
    canvas.style.transformOrigin = '0 0';
    canvas.style.transform = resizePreviewTransform(x, y);
    this.resizePreviewActive = true;
    this.syncSpotlightGestureTransform();
  }

  /**
   * The canvas's untransformed layout on each axis, measured from the drawing area's visible edge.
   * offsetLeft and offsetTop ignore transforms, so this reads the layout even while a preview is
   * drawn.
   */
  private resizePreviewAxes(
    canvas: HTMLCanvasElement,
    container: HTMLElement,
    target: { width: number; height: number }
  ): [PreviewAxis, PreviewAxis] {
    const wrapper = this.canvasWrapper?.nativeElement;
    const margin = PdfRenderService.CENTERING_PADDING_PX;
    return [
      {
        viewport: container.clientWidth,
        start: canvas.offsetLeft + (wrapper?.offsetLeft ?? 0) - container.scrollLeft,
        size: canvas.offsetWidth,
        targetSize: target.width,
        margin,
      },
      {
        viewport: container.clientHeight,
        start: canvas.offsetTop + (wrapper?.offsetTop ?? 0) - container.scrollTop,
        size: canvas.offsetHeight,
        targetSize: target.height,
        margin,
      },
    ];
  }

  /** Removes the resize preview transform from the canvas and the overlays. */
  private clearResizePreview(): void {
    if (!this.resizePreviewActive) return;
    this.resizePreviewActive = false;
    const canvas = this.pdfCanvas?.nativeElement;
    if (canvas) {
      canvas.style.transition = 'none';
      canvas.style.transform = '';
      canvas.style.transformOrigin = '';
    }
    this.syncSpotlightGestureTransform();
    this.updateSpotlightValues();
  }

  /**
   * Drops a pending re-fit and its preview because the reviewer started zooming: they are choosing
   * their own zoom now, and a gesture must start from the page as laid out.
   */
  private cancelResizeRefit(): void {
    // A fading old picture does not follow a gesture; drop it rather than leave it behind (Issue #3245).
    this.pdfRenderService.cancelSwapFade();
    if (this.stickyFitTimer) {
      clearTimeout(this.stickyFitTimer);
      this.stickyFitTimer = null;
    }
    this.refitGeneration++;
    this.clearResizePreview();
  }

  /**
   * Renders one fit mode again at the drawing area's settled size. The preview transform is cleared
   * in the same step that swaps the new bitmap in, so the page sharpens without moving.
   */
  private async refit(mode: PreviewFitMode): Promise<void> {
    if (this.isLoading || this.isGestureZooming || !this.currentPage) {
      this.clearResizePreview();
      return;
    }
    const generation = this.refitGeneration;
    this.refitInFlight = true;
    try {
      if (mode === 'viewport') await this.fitToViewport(() => this.clearResizePreview());
      else await this.refitKeepingViewCentre(mode);
    } finally {
      this.refitInFlight = false;
    }
    // A cancelled render never reaches its swap. Clear the preview here unless a newer size has
    // already drawn its own.
    if (generation === this.refitGeneration) this.clearResizePreview();
    this.cdr.markForCheck();
  }

  /**
   * Fit to width or height after a resize. The page overflows the view on its other axis, so the
   * point in the middle of the view stays there, as it did in the preview, rather than the page
   * being re-centred, which used to move a reader away from the part of the sheet they were on.
   */
  private async refitKeepingViewCentre(mode: 'width' | 'height'): Promise<void> {
    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    const scale = this.pdfRenderService.getFitScale(mode);
    if (!canvas || !container || scale === null) return;

    const [x, y] = this.resizePreviewAxes(canvas, container, { width: 0, height: 0 });
    const rect = container.getBoundingClientRect();
    await this.pdfRenderService.setZoom(scale, {
      focalScreenPoint: {
        x: rect.left + container.clientLeft + x.viewport / 2,
        y: rect.top + container.clientTop + y.viewport / 2,
      },
      canvasPoint: { x: viewCentreOnCanvas(x), y: viewCentreOnCanvas(y) },
      onBeforeSwap: () => this.clearResizePreview(),
    });
    this.updateScaleValues();
    this.paintedScale = this.currentScale;
    this.fittedTo = { mode, scale: this.currentScale };
  }

  // Fast CSS-based zooming for gesture feedback
  private setGestureZoom(scale: number): void {
    if (!this.pdfCanvas?.nativeElement) return;

    const canvas = this.pdfCanvas.nativeElement;
    const scaleRatio = scale / this.gestureStartScale;

    const baseTransform = this.gestureBaseTransform || 'translate3d(0px, 0px, 0)';
    canvas.style.transform = `${baseTransform} scale3d(${scaleRatio}, ${scaleRatio}, 1)`;
    canvas.style.transition ||= 'none';

    const cx = this.gestureFocalPointCanvas.x;
    const cy = this.gestureFocalPointCanvas.y;
    canvas.style.transformOrigin = `${cx}px ${cy}px`;

    // Drag the stencil along with the drawing on this same frame (#1489)
    this.syncSpotlightGestureTransform();

    // Update gesture scale (don't touch currentScale to avoid triggering renders)
    this.gestureCurrentScale = scale;
  }

  // Commit the gesture zoom with a full re-render
  private async commitGestureZoom(
    scale: number,
    focalXScreen?: number,
    focalYScreen?: number,
    unscaledCx?: number,
    unscaledCy?: number
  ): Promise<void> {
    if (!this.pdfCanvas?.nativeElement) return;

    // Do the full high-DPI re-render. We pass a callback to reset CSS transforms
    // exactly when the canvas size and scroll have been synchronously updated,
    // to prevent any visual flashing or jumping.
    await this.pdfRenderService.setZoom(scale, {
      focalScreenPoint:
        focalXScreen !== undefined && focalYScreen !== undefined ? { x: focalXScreen, y: focalYScreen } : undefined,
      canvasPoint: unscaledCx !== undefined && unscaledCy !== undefined ? { x: unscaledCx, y: unscaledCy } : undefined,
      onBeforeSwap: () => {
        this.pdfCanvas.nativeElement.style.transition = 'none';
        this.pdfCanvas.nativeElement.style.transform = '';
        this.pdfCanvas.nativeElement.style.transformOrigin = '';

        // The canvas already has its new CSS size here but has not painted yet, so
        // this is the one frame where the stencil can hand off from the mirrored
        // transform to real geometry without a visible snap. Clear the mirror first,
        // then re-measure — updateSpotlightValues() only recomputes once the canvas
        // transform is gone. Issue #1489.
        this.syncSpotlightGestureTransform();
        this.updateSpotlightValues();
      },
      resolveFocalTarget: () => this.resolveGestureFocalPointScreen(),
    });

    // Defensive: if the render was cancelled the swap callback never ran, which
    // would strand the gesture transform on both canvas and stencil and freeze
    // geometry updates. Only safe once no gesture is in flight.
    // The view can be destroyed during the await above, so re-read the ViewChild
    // defensively instead of trusting the non-null assertion from entry.
    if (!this.isGestureZooming && this.isCanvasTransformActive()) {
      const canvas = this.pdfCanvas?.nativeElement;
      if (canvas) {
        // Clear the transition first, matching the normal swap-callback path.
        // Otherwise a leftover non-'none' transition would animate the transform
        // being cleared, and the mirrored stencil would inherit that too.
        canvas.style.transition = 'none';
        canvas.style.transform = '';
        canvas.style.transformOrigin = '';
        this.syncSpotlightGestureTransform();
      }
    }

    this.updateScaleValues();
  }

  /**
   * Smoothly correct scroll position if it's beyond the valid range.
   * Called after pinch gesture commits where the focal-point offset may leave
   * empty background space. Uses scroll-behavior:smooth for a natural fill-in
   * animation. Safe because no CSS transform animation is running at this point.
   */
  private smoothClampScroll(): void {
    const container = this.pdfContainer?.nativeElement;
    if (!container) return;

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

    // Only animate if scroll is actually out of bounds
    const needsClampX = container.scrollLeft > maxScrollLeft || container.scrollLeft < 0;
    const needsClampY = container.scrollTop > maxScrollTop || container.scrollTop < 0;

    ClientLoggingService.log('DEBUG', '[Zoom] smoothClampScroll:', {
      scrollLeft: Math.round(container.scrollLeft),
      scrollTop: Math.round(container.scrollTop),
      maxScrollLeft,
      maxScrollTop,
      needsClampX,
      needsClampY,
      scrollBehaviorBefore: container.style.scrollBehavior,
    });

    if (!needsClampX && !needsClampY) return;

    const originalScrollBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = 'smooth';

    const targetLeft = needsClampX ? Math.max(0, Math.min(maxScrollLeft, container.scrollLeft)) : container.scrollLeft;
    const targetTop = needsClampY ? Math.max(0, Math.min(maxScrollTop, container.scrollTop)) : container.scrollTop;

    ClientLoggingService.log('DEBUG', '[Zoom] smoothClampScroll applying:', {
      from: { left: Math.round(container.scrollLeft), top: Math.round(container.scrollTop) },
      to: { left: Math.round(targetLeft), top: Math.round(targetTop) },
      scrollBehavior: container.style.scrollBehavior,
    });

    container.scrollLeft = targetLeft;
    container.scrollTop = targetTop;

    // Restore after a delay to let the smooth scroll animation complete.
    if (this.scrollBehaviorRestoreTimeout) {
      clearTimeout(this.scrollBehaviorRestoreTimeout);
    }
    this.scrollBehaviorRestoreTimeout = setTimeout(() => {
      container.style.scrollBehavior = originalScrollBehavior;
      this.scrollBehaviorRestoreTimeout = null;
    }, 300);
  }

  /** Clamps the container's scroll position to the valid range [0..maxScroll]. */
  private clampScrollPosition(container: HTMLElement): void {
    const maxSL = Math.max(0, container.scrollWidth - container.clientWidth);
    const maxST = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollLeft = Math.max(0, Math.min(maxSL, container.scrollLeft));
    container.scrollTop = Math.max(0, Math.min(maxST, container.scrollTop));
  }

  /** Freezes scroll behavior and cancels any pending restore timeout. */
  private freezeScrollBehavior(container: HTMLElement): void {
    if (this.scrollBehaviorRestoreTimeout) {
      clearTimeout(this.scrollBehaviorRestoreTimeout);
      this.scrollBehaviorRestoreTimeout = null;
    }
    container.style.scrollBehavior = 'auto';
  }

  // Gesture handling methods
  private setupGestureHandling(): void {
    const container = this.pdfContainer.nativeElement;

    // Mouse events for drag-to-pan
    container.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));

    // Touch events for pinch-to-zoom and pan
    container.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    container.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    container.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });

    // Wheel events for zoom
    container.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
  }

  private onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return; // Only left mouse button

    this.previewFocusService.cancelCurrentFocus('User pan interaction');

    // Issue #1212: Suppress drag-to-pan when a measurement tool is active
    if (this.activeTool) return;

    const textHit = this.textLayerHit(event.target, event.clientX, event.clientY);
    if (textHit === 'action-bar') return;
    // Issue #3338: Alt (Option on a Mac) is the text tool. While it is held, a press on a word
    // selects text the browser's way (a drag selects, a double click selects the word), and a press
    // elsewhere neither pans nor counts as a tap.
    if (event.altKey) return;
    // A touch screen emulates mouse events after a tap on a word, which the touch handlers already
    // handled; the browser's own long-press selection must not be cancelled either.
    if (textHit === 'text' && PreviewComponent.firedByTouch(event)) return;
    // Without Alt a word is part of the drawing (Issue #3338): a drag that starts on it pans, a
    // click is a tap, and a double click zooms. Preventing the press's default below also stops the
    // browser from selecting the word.

    // Record tap start position for tap detection
    this.tapStartPosition = { x: event.clientX, y: event.clientY };
    this.isMouseDown = true;

    // Don't immediately set isDragging - wait for significant movement
    this.lastMousePosition = { x: event.clientX, y: event.clientY };

    const container = this.pdfContainer.nativeElement;
    this.initialScrollLeft = container.scrollLeft;
    this.initialScrollTop = container.scrollTop;

    // Debug info
    ClientLoggingService.log('INFO', 'Mouse down at:', event.clientX, event.clientY);

    event.preventDefault();
    event.stopPropagation();
  }

  private onMouseMove(event: MouseEvent): void {
    // Alt may have been pressed or released while another window had the focus.
    this.setAltHeld(event.altKey);
    if (!this.isMouseDown) return; // Only track movement if mouse is down

    // Check if we should start dragging (significant movement)
    if (!this.isDragging) {
      const deltaX = Math.abs(event.clientX - this.tapStartPosition.x);
      const deltaY = Math.abs(event.clientY - this.tapStartPosition.y);

      if (deltaX > this.TAP_THRESHOLD || deltaY > this.TAP_THRESHOLD) {
        this.isDragging = true;
        const container = this.pdfContainer.nativeElement;
        container.style.scrollBehavior = 'auto';
        container.style.cursor = 'grabbing';
        ClientLoggingService.log('INFO', 'Started dragging due to movement:', deltaX, deltaY);
      } else {
        return; // Not enough movement to start dragging
      }
    }

    const deltaX = event.clientX - this.lastMousePosition.x;
    const deltaY = event.clientY - this.lastMousePosition.y;

    const container = this.pdfContainer.nativeElement;

    // Clamp scroll values to valid ranges
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const maxScrollTop = container.scrollHeight - container.clientHeight;

    const newScrollLeft = Math.max(0, Math.min(maxScrollLeft, this.initialScrollLeft - deltaX));
    const newScrollTop = Math.max(0, Math.min(maxScrollTop, this.initialScrollTop - deltaY));

    // Use scrollTo for more reliable scrolling
    container.scrollTo(newScrollLeft, newScrollTop);

    // Debug info (only log occasionally)
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      ClientLoggingService.log('INFO', 'Dragging - Delta:', deltaX, ',', deltaY);
      ClientLoggingService.log('INFO', 'Dragging - Target scroll:', newScrollLeft, ',', newScrollTop);
      ClientLoggingService.log('INFO', 'Dragging - Max scroll:', maxScrollLeft, ',', maxScrollTop);
      ClientLoggingService.log('INFO', 'Dragging - Actual scroll:', container.scrollLeft, ',', container.scrollTop);
    }

    event.preventDefault();
    event.stopPropagation();
  }

  private onMouseUp(event: MouseEvent): void {
    if (!this.isMouseDown) return;

    if (this.isDragging) {
      // Was dragging - reset drag state
      this.isDragging = false;
      const container = this.pdfContainer.nativeElement;
      // Back to the stylesheet's cursor: the hand, or the I-beam while Alt is held (Issue #3338).
      container.style.cursor = '';
      // Re-enable smooth scrolling
      container.style.scrollBehavior = 'smooth';
      window.getSelection()?.removeAllRanges();
    } else {
      // Was not dragging - this is a simple click/tap
      ClientLoggingService.log('INFO', 'Simple click detected!');
      this.onCanvasTap(event);
    }

    this.isMouseDown = false;
  }

  private onTouchStart(event: TouchEvent): void {
    this.previewFocusService.cancelCurrentFocus('User touch interaction');
    // Only a single touch that starts on a word can be a tap on it. A second finger turns it into a
    // pinch, and a touch the browser cancelled never ends; neither may hold back the next touch end.
    this.textTouch = null;

    // Issue #1212: Suppress touch-to-pan/zoom when a measurement tool is active.
    // Issue #1747: Select area for chat draws with one finger but keeps two-finger pinch zoom.
    if (this.activeTool && !(this.activeTool === 'area' && event.touches.length === 2)) return;

    if (event.touches.length === 1) {
      // Issue #1745: Allow native single-touch text selection on text layer without starting canvas drag-pan
      const touch = event.touches[0];
      const target = event.target ?? touch?.target ?? null;
      const textHit = this.textLayerHit(target, touch?.clientX, touch?.clientY);
      if (textHit === 'action-bar') return;
      if (textHit === 'text') {
        // A long press still selects natively; a short tap may be half of a double tap (Issue #3312).
        this.textTouch = { x: touch.clientX, y: touch.clientY, time: PreviewComponent.eventTime(event) };
        return;
      }

      // Single touch outside text layer - start pan
      this.isDragging = true;

      // Record tap start position for tap detection
      this.tapStartPosition = { x: touch.clientX, y: touch.clientY };

      this.lastMousePosition = { x: touch.clientX, y: touch.clientY };

      const container = this.pdfContainer.nativeElement;
      this.initialScrollLeft = container.scrollLeft;
      this.initialScrollTop = container.scrollTop;

      // Disable smooth scrolling during drag
      container.style.scrollBehavior = 'auto';
    } else if (event.touches.length === 2) {
      // Two touches - start pinch-to-zoom (preserved across entire canvas including text layer)
      this.isZooming = true;
      this.isDragging = false;
      this.cancelResizeRefit();
      this.isGestureZooming = true;

      if (this.zoomTimeout) {
        clearTimeout(this.zoomTimeout);
        this.zoomTimeout = null;
      }

      this.pdfRenderService.cancelCurrentRender(); // Abort ongoing renders
      this.initialScale = this.gestureCurrentScale; // Continue from current visual scale
      this.gestureStartScale = this.paintedScale; // Keep base as the actual painted canvas

      const container = this.pdfContainer.nativeElement;
      // Freeze scroll during the gesture to prevent browser-driven drift
      this.freezeScrollBehavior(container);
      this.clampScrollPosition(container);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      this.initialTouchDistance = this.getTouchDistance(touch1, touch2);
      this.lastTouchDistance = this.initialTouchDistance;
      this.lastTouchCenter = this.getTouchCenter(touch1, touch2);

      const canvasRect = this.pdfCanvas.nativeElement.getBoundingClientRect();
      this.gestureBaseTransform = this.pdfCanvas.nativeElement.style.transform;
      this.gestureUntransformedRect = canvasRect; // Cache clean rect for bounce-back animation
      this.recordGestureFocalPoint(container, this.lastTouchCenter.x, this.lastTouchCenter.y);
      this.gestureFocalPointCanvas = {
        x: this.lastTouchCenter.x - canvasRect.left,
        y: this.lastTouchCenter.y - canvasRect.top,
      };
      this.pdfCanvas.nativeElement.style.transition = 'none'; // Ensure no transition during pinch
    }

    event.preventDefault();
    event.stopPropagation();
  }

  private onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 1 && this.isDragging) {
      // Single touch - pan
      const touch = event.touches[0];
      const deltaX = touch.clientX - this.lastMousePosition.x;
      const deltaY = touch.clientY - this.lastMousePosition.y;

      const container = this.pdfContainer.nativeElement;

      // Clamp scroll values to valid ranges
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const maxScrollTop = container.scrollHeight - container.clientHeight;

      const newScrollLeft = Math.max(0, Math.min(maxScrollLeft, this.initialScrollLeft - deltaX));
      const newScrollTop = Math.max(0, Math.min(maxScrollTop, this.initialScrollTop - deltaY));

      // Use scrollTo for more reliable scrolling
      container.scrollTo(newScrollLeft, newScrollTop);
      event.preventDefault();
    } else if (event.touches.length === 2 && this.isZooming) {
      // Two touches - pinch-to-zoom
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      const currentDistance = this.getTouchDistance(touch1, touch2);

      if (this.initialTouchDistance > 0) {
        // Calculate raw scale based on initial distance for more stable scaling
        const scaleFactor = currentDistance / this.initialTouchDistance;
        const rawScale = this.initialScale * scaleFactor;

        // Apply rubber-band resistance if zoomed past boundaries
        const newScale = Math.max(0.1, this.applyRubberBand(rawScale));

        if (this.isGestureZooming) {
          // Use requestAnimationFrame for smooth updates
          if (this.zoomAnimationFrame) {
            cancelAnimationFrame(this.zoomAnimationFrame);
          }

          this.zoomAnimationFrame = requestAnimationFrame(() => {
            this.setGestureZoom(newScale);
            this.zoomAnimationFrame = null;
          });
        }
      }

      this.lastTouchDistance = currentDistance;
      event.preventDefault();
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    if (this.textTouch && event.touches.length === 0) {
      const press = this.textTouch;
      this.textTouch = null;
      const touch = event.changedTouches[0];
      const short = PreviewComponent.eventTime(event) - press.time < PreviewComponent.LONG_PRESS_MS;
      if (touch && short && PreviewComponent.withinTap(press, touch.clientX, touch.clientY, this.TAP_THRESHOLD)) {
        // The tap is handled here; the browser's own double-tap word selection and the mouse
        // events it would emulate afterwards would act on it a second time.
        event.preventDefault();
        this.onTextLayerTap(event);
      }
      return;
    }
    if (event.touches.length === 0) {
      // Check if this was a tap (minimal movement) for single touch
      if (this.isDragging && !this.isGestureZooming && this.isTapGesture(event)) {
        this.onCanvasTap(event);
      }

      // Cancel any pending zoom animation
      if (this.zoomAnimationFrame) {
        cancelAnimationFrame(this.zoomAnimationFrame);
        this.zoomAnimationFrame = null;
      }

      // Gesture ended - commit the final zoom if we were gesture zooming
      if (this.isGestureZooming) {
        // If unzoomed past base scale or overzoomed past max scale, glide back smoothly
        const maxScale = this.getMaximumZoomScale();
        if (this.gestureCurrentScale < this.baseScale) {
          // Keep isGestureZooming = true so applySmoothZoom continues the existing gesture smoothly
          this.applySmoothZoom(this.baseScale, undefined, undefined, true, true);
        } else if (this.gestureCurrentScale > maxScale) {
          this.applySmoothZoom(maxScale, undefined, undefined, false, true);
        } else {
          const settledFocalPoint = this.resolveGestureFocalPointScreen();
          this.commitGestureZoom(
            this.gestureCurrentScale,
            settledFocalPoint.x,
            settledFocalPoint.y,
            this.gestureFocalPointCanvas.x,
            this.gestureFocalPointCanvas.y
          ).then(() => {
            // Defer to next frame so the DOM has recalculated scrollWidth/scrollHeight
            // after the canvas re-render before we check bounds.
            requestAnimationFrame(() => this.smoothClampScroll());
          });
          this.isGestureZooming = false;
        }
      }

      // Re-enable smooth scrolling
      if (this.isDragging) {
        this.pdfContainer.nativeElement.style.scrollBehavior = 'smooth';
      }

      this.isDragging = false;
      this.isZooming = false;
      this.lastTouchDistance = 0;
      this.initialTouchDistance = 0;
    } else if (event.touches.length === 1) {
      // Cancel any pending zoom animation
      if (this.zoomAnimationFrame) {
        cancelAnimationFrame(this.zoomAnimationFrame);
        this.zoomAnimationFrame = null;
      }

      // Switch from zoom to pan only if active zooming was in progress
      if (this.isZooming) {
        this.isZooming = false;
        this.isDragging = true;
        const touch = event.touches[0];
        this.lastMousePosition = { x: touch.clientX, y: touch.clientY };

        const container = this.pdfContainer.nativeElement;
        this.initialScrollLeft = container.scrollLeft;
        this.initialScrollTop = container.scrollTop;

        // Disable smooth scrolling during drag
        container.style.scrollBehavior = 'auto';
      }
    }
  }

  private onWheel(event: WheelEvent): void {
    this.previewFocusService.cancelCurrentFocus('User wheel interaction');
    // Handle trackpad pinch-to-zoom (gesture events)
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();

      // Start gesture zooming if not already active
      if (!this.isGestureZooming) {
        this.cancelResizeRefit();
        this.pdfRenderService.cancelCurrentRender(); // Abort ongoing renders
        this.isGestureZooming = true;
        this.gestureStartScale = this.paintedScale;
        this.gestureRawScale = this.gestureCurrentScale; // Start raw tracking from current visual scale

        const container = this.pdfContainer.nativeElement;
        // Freeze scroll during the gesture to prevent browser-driven drift
        this.freezeScrollBehavior(container);
        // Clamp scroll to valid range — a previous zoom commit
        // may have left scrollLeft beyond the new maxScrollLeft.
        this.clampScrollPosition(container);

        const canvasRect = this.pdfCanvas.nativeElement.getBoundingClientRect();
        this.gestureBaseTransform = this.pdfCanvas.nativeElement.style.transform;
        this.gestureUntransformedRect = canvasRect; // Cache clean rect for bounce-back animation
        this.recordGestureFocalPoint(container, event.clientX, event.clientY);
        this.gestureFocalPointCanvas = {
          x: event.clientX - canvasRect.left,
          y: event.clientY - canvasRect.top,
        };
        this.pdfCanvas.nativeElement.style.transition = 'none'; // Ensure no transition during trackpad pinch
      }

      // Smooth zooming formula: handles both trackpad (small delta) and mouse wheel (large delta)
      let normalizedDeltaY = event.deltaY;

      // Mice emit large chunks per click (e.g., 100), trackpads emit small chunks continuously.
      // We cap the large chunks to prevent coarse jumps, but use a high multiplier for the trackpad.
      if (Math.abs(normalizedDeltaY) >= 50) {
        normalizedDeltaY = Math.sign(normalizedDeltaY) * 25;
      }

      // zoomIntensity = 0.007 gives great trackpad sensitivity.
      // 25 * 0.007 = 0.175 -> exp(-0.175) = ~16% step per mouse click.
      const zoomIntensity = 0.007;
      const delta = Math.exp(-normalizedDeltaY * zoomIntensity);

      // Track the raw undamped scale to avoid compounding rubber-band distortion
      this.gestureRawScale *= delta;
      const newScale = Math.max(0.1, this.applyRubberBand(this.gestureRawScale));
      this.gestureCurrentScale = newScale;

      // Ensure we run the CSS transform updates smoothly
      if (this.zoomAnimationFrame) {
        cancelAnimationFrame(this.zoomAnimationFrame);
      }
      this.zoomAnimationFrame = requestAnimationFrame(() => {
        this.setGestureZoom(this.gestureCurrentScale);
        this.zoomAnimationFrame = null;
      });

      // Clear any pending zoom timeout
      if (this.zoomTimeout) {
        clearTimeout(this.zoomTimeout);
      }

      // Debounce the zoom commit operation — with bounce-back if past boundaries
      this.zoomTimeout = setTimeout(() => {
        if (this.gestureCurrentScale < this.baseScale) {
          this.applySmoothZoom(this.baseScale, undefined, undefined, true, true);
        } else if (this.gestureCurrentScale > this.getMaximumZoomScale()) {
          this.applySmoothZoom(this.getMaximumZoomScale(), undefined, undefined, false, true);
        } else {
          this.isGestureZooming = false;
          this.gestureRawScale = this.gestureCurrentScale; // Reset raw to match committed
          const settledFocalPoint = this.resolveGestureFocalPointScreen();
          this.commitGestureZoom(
            this.gestureCurrentScale,
            settledFocalPoint.x,
            settledFocalPoint.y,
            this.gestureFocalPointCanvas.x,
            this.gestureFocalPointCanvas.y
          ).then(() => {
            requestAnimationFrame(() => this.smoothClampScroll());
          });
        }
      }, 150);
    }
  }

  private getTouchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getTouchCenter(touch1: Touch, touch2: Touch): { x: number; y: number } {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  }

  ngOnDestroy(): void {
    this.endSpotlightFlight();
    window.removeEventListener('keydown', this.onDocumentKeydownListener, { capture: true });
    window.removeEventListener('keydown', this.onAltKeyListener, { capture: true });
    window.removeEventListener('keyup', this.onAltKeyListener, { capture: true });
    window.removeEventListener('blur', this.onWindowBlurListener);
    this.unbindDiagnostics();
    if (this.findTimer) clearTimeout(this.findTimer);
    this.textFocus.clear();
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    if (this.stickyFitTimer) clearTimeout(this.stickyFitTimer);
    this.pdfContainer?.nativeElement?.removeEventListener('scroll', this.onPdfContainerScroll);

    // Clear any pending zoom timeout
    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout);
    }

    if (this.focusAnimationFrame !== null) {
      cancelAnimationFrame(this.focusAnimationFrame);
      this.focusAnimationFrame = null;
    }
    if (this.focusAnimationTimeout) {
      clearTimeout(this.focusAnimationTimeout);
      this.focusAnimationTimeout = null;
    }
    const container = this.pdfContainer?.nativeElement;
    if (container && this.focusOriginalScrollBehavior !== null) {
      container.style.scrollBehavior = this.focusOriginalScrollBehavior;
      this.focusOriginalScrollBehavior = null;
    }
    this.previewFocusService.cancelCurrentFocus('Component destroyed');

    // Clear any pending scroll behavior restore timeout
    if (this.scrollBehaviorRestoreTimeout) {
      clearTimeout(this.scrollBehaviorRestoreTimeout);
    }

    // Cancel any pending zoom animation
    if (this.zoomAnimationFrame) {
      cancelAnimationFrame(this.zoomAnimationFrame);
    }

    if (this.findTimer) {
      clearTimeout(this.findTimer);
    }

    // Clear tap timeout
    if (this.tapTimeout) {
      clearTimeout(this.tapTimeout);
    }

    this.pdfRenderService.cancelCurrentRender();
  }

  // Tap detection methods
  /**
   * Tells a double tap (zoom) from a single tap (toggle the toolbar).
   *
   * The gap between two taps is measured between the times the taps happened (each event's
   * timeStamp), not the times their handlers ran. Each tap starts an app-wide Angular update,
   * and while the browser is busy the second release waits in the queue; timed by its handler, a
   * real double click 220 ms apart could measure over 300 ms and become two single taps, so the
   * page did not zoom and the toolbar flickered instead (Issue #3309).
   */
  private onCanvasTap(event: MouseEvent | TouchEvent): void {
    const tapTime = PreviewComponent.eventTime(event);
    const tapLength = this.lastTapTime === null ? Number.POSITIVE_INFINITY : tapTime - this.lastTapTime;

    if (tapLength < PreviewComponent.DOUBLE_TAP_MS && tapLength > 0) {
      // Double tap detected
      if (this.tapTimeout) {
        clearTimeout(this.tapTimeout);
        this.tapTimeout = null;
      } else if (this.lastTapToggledToolbar) {
        // The first tap's toolbar toggle already ran before this tap was handled; undo it.
        this.toggleToolbar();
      }
      this.handleDoubleTap(event);
      this.lastTapTime = null;
      this.lastTapToggledToolbar = false;
    } else {
      // A single tap. A previous single tap's action still waiting runs now, so its timer is not
      // lost and cannot fire later in the middle of a double-tap zoom.
      if (this.tapTimeout) {
        clearTimeout(this.tapTimeout);
        this.tapTimeout = null;
        this.toggleToolbar();
      }
      // Debounced to wait for a potential second tap.
      this.lastTapToggledToolbar = false;
      this.tapTimeout = setTimeout(() => {
        const eventType = event.type;
        const isTouchEvent = 'touches' in event;
        ClientLoggingService.log('INFO', `Canvas tap detected: ${eventType} (${isTouchEvent ? 'touch' : 'mouse'})`);
        this.toggleToolbar();
        this.tapTimeout = null;
        this.lastTapToggledToolbar = true;
      }, PreviewComponent.DOUBLE_TAP_MS);
      this.lastTapTime = tapTime;
    }
  }

  /** Forgets the last tap and drops its pending single-tap action. */
  private resetTapState(): void {
    if (this.tapTimeout) {
      clearTimeout(this.tapTimeout);
      this.tapTimeout = null;
    }
    this.lastTapTime = null;
    this.lastTapToggledToolbar = false;
    this.textTouch = null;
  }

  /**
   * What a press lands on in the selectable text layer (Issue #1745): a word, the selection's
   * action bar, or neither. The pointer is checked as well as the target, because the layer's
   * words sit above the canvas while the event may be retargeted to the container.
   */
  private textLayerHit(target: EventTarget | null, clientX?: number, clientY?: number): 'text' | 'action-bar' | null {
    const classify = (el: Element | null): 'text' | 'action-bar' | null => {
      if (!el) return null;
      if (el.closest('.selection-action-bar')) return 'action-bar';
      return el.closest('.text-unit-span, .text-selection-layer') ? 'text' : null;
    };
    const hit = classify(target instanceof Element ? target : null);
    if (hit) return hit;
    if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
    if (clientX == null || clientY == null) return null;
    return classify(document.elementFromPoint(clientX, clientY));
  }

  /** Whether a mouse event was emulated by the browser after a touch. */
  private static firedByTouch(event: MouseEvent): boolean {
    return !!(event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities
      ?.firesTouchEvents;
  }

  /** Records whether Alt is held, and updates the cursor when that changes (Issue #3338). */
  private setAltHeld(held: boolean): void {
    if (held === this.altHeld) return;
    this.ngZone.run(() => {
      this.altHeld = held;
      this.cdr.markForCheck();
    });
  }

  /**
   * A short touch on a word that did not move. The second tap of a double tap zooms through the
   * same path as on bare drawing, whichever of the two taps landed on a word. A single tap on a
   * word still does nothing of its own, as before: it does not toggle the toolbar, and is only
   * remembered as the possible first half of a double tap. (A mouse press on a word without Alt
   * goes through the drawing's own press handling instead; Issue #3338.)
   */
  private onTextLayerTap(event: TouchEvent): void {
    const tapTime = PreviewComponent.eventTime(event);
    const tapLength = this.lastTapTime === null ? Number.POSITIVE_INFINITY : tapTime - this.lastTapTime;
    if (tapLength < PreviewComponent.DOUBLE_TAP_MS && tapLength > 0) {
      window.getSelection()?.removeAllRanges();
      this.onCanvasTap(event);
      return;
    }
    // An earlier single tap's toolbar toggle still waiting runs now, as in onCanvasTap.
    if (this.tapTimeout) {
      clearTimeout(this.tapTimeout);
      this.tapTimeout = null;
      this.toggleToolbar();
    }
    // A touch tap cancelled the browser's default, which would have cleared a selection on a click.
    if ('touches' in event) window.getSelection()?.removeAllRanges();
    this.lastTapTime = tapTime;
    this.lastTapToggledToolbar = false;
  }

  private static withinTap(start: { x: number; y: number }, x: number, y: number, threshold: number): boolean {
    return Math.abs(x - start.x) < threshold && Math.abs(y - start.y) < threshold;
  }

  /** When an input event happened, on the performance.now() clock that event timeStamps use. */
  private static eventTime(event: Event): number {
    const stamp = event.timeStamp;
    return Number.isFinite(stamp) && stamp > 0 ? stamp : performance.now();
  }

  private handleDoubleTap(event: MouseEvent | TouchEvent): void {
    let clientX: number, clientY: number;
    if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      const touches = event.changedTouches;
      if (touches.length === 0) return;
      clientX = touches[0].clientX;
      clientY = touches[0].clientY;
    }

    const isShiftHeld = event instanceof MouseEvent && event.shiftKey;
    const maximumScale = this.getMaximumZoomScale();
    const scaleTolerance = Math.max(0.001, this.baseScale * 0.05);
    const isFitToScreen = Math.abs(this.gestureCurrentScale - this.baseScale) < scaleTolerance;
    const isMaxZoom = Math.abs(this.gestureCurrentScale - maximumScale) < scaleTolerance;

    if (isShiftHeld) {
      // Shift+double-tap: toggle between max zoom and fit-to-screen
      if (isMaxZoom) {
        this.applySmoothZoom(this.baseScale, undefined, undefined, true);
      } else {
        this.applySmoothZoom(maximumScale, clientX, clientY, false);
      }
    } else if (isFitToScreen) {
      // A regular double-tap lands just below the sharp-render ceiling. This is
      // close to the former Shift+double-tap behavior, while the modified
      // gesture can continue to the closer user-facing maximum.
      const maximumRenderScale = Math.max(this.baseScale, this.pdfRenderService.getMaximumRenderScale());
      const targetScale = this.baseScale + (maximumRenderScale - this.baseScale) * 0.85;
      this.applySmoothZoom(targetScale, clientX, clientY, false);
    } else {
      // Unzoom back to fit-to-screen
      this.applySmoothZoom(this.baseScale, undefined, undefined, true);
    }
  }

  private toggleToolbar(): void {
    ClientLoggingService.log('INFO', 'Toggling toolbar - current visibility:', this.pdfToolbar?.isVisible);
    if (this.pdfToolbar) {
      if (this.pdfToolbar.isVisible) {
        this.pdfToolbar.forceHide();
      } else {
        this.pdfToolbar.forceShow();
      }
    }
  }

  private isTapGesture(event: MouseEvent | TouchEvent): boolean {
    let clientX: number, clientY: number;

    if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      // TouchEvent - use changedTouches for touchend events
      const touches = event.touches.length > 0 ? event.touches : event.changedTouches;
      if (touches.length !== 1) return false; // Only single touch
      clientX = touches[0].clientX;
      clientY = touches[0].clientY;
    }

    const deltaX = Math.abs(clientX - this.tapStartPosition.x);
    const deltaY = Math.abs(clientY - this.tapStartPosition.y);

    ClientLoggingService.log('INFO', 'Tap gesture check - delta:', deltaX, deltaY, 'threshold:', this.TAP_THRESHOLD);
    return deltaX < this.TAP_THRESHOLD && deltaY < this.TAP_THRESHOLD;
  }

  /**
   * Parse highlight parameter from URL.
   * Format: "x,y,width,height" (all in pixels)
   * Example: "4838,3420,705,873"
   */
  private parseHighlightParam(param: string): BoundingBox | null {
    const parts = param.split(',').map(p => parseInt(p, 10));

    if (parts.length !== 4 || parts.some(isNaN)) {
      ClientLoggingService.log('WARN', '[Preview] Invalid highlight parameter:', param);
      return null;
    }

    return {
      x: parts[0],
      y: parts[1],
      width: parts[2],
      height: parts[3],
    };
  }

  /**
   * Dismiss spotlight highlight (remove from URL).
   */
  onDismissHighlight(): void {
    ClientLoggingService.log('INFO', '[Preview] Dismissing highlight');
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: highlightQueryParams(NO_HIGHLIGHT_REGIONS),
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Get wrapper width (the full scrollable container).
   * Use canvasWrapper for width (respects horizontal scrolling).
   */
  private getWrapperWidth(): number {
    return this.canvasWrapper?.nativeElement?.clientWidth || 0;
  }

  /**
   * Get wrapper height (the full scrollable container).
   * Use pdfContainer for height (full viewport height).
   */
  private getWrapperHeight(): number {
    return this.pdfContainer?.nativeElement?.clientHeight || 0;
  }

  /**
   * Get canvas display width (CSS rendered width).
   */
  private getCanvasWidth(): number {
    return this.pdfCanvas?.nativeElement?.clientWidth || 0;
  }

  /**
   * Get canvas display height (CSS rendered height).
   */
  private getCanvasHeight(): number {
    return this.pdfCanvas?.nativeElement?.clientHeight || 0;
  }

  /**
   * Get canvas scale for highlight coordinates.
   *
   * Highlight box coordinates are in original PDF space (scale 1.0).
   * Canvas is rendered at currentScale * 2 (for high-DPI), displayed at currentScale.
   * So we need to scale highlights by: currentScale / 2 (to match canvas display size).
   */
  private getCanvasScale(): number {
    // Get the actual PDF rendering scale from the service. During a large zoom-out flight the page is
    // laid out at its widest view instead (Issue #2924), and the highlight windows must use that
    // layout, or they are placed for a page several times larger and fall outside it (Issue #3127).
    const pdfScale = this.focusLayoutScale ?? this.pdfRenderService.getCurrentScale();

    // Canvas is rendered at 2x for high-DPI, displayed at 1x
    // So display scale relative to original PDF = pdfScale / 2
    const displayScale = pdfScale / 2;

    return displayScale;
  }

  /** Returns the canvas X position in the container's scrollable coordinate space. */
  private getCanvasOffsetX(): number {
    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    if (!canvas || !container) return 0;

    return canvas.getBoundingClientRect().left - container.getBoundingClientRect().left + container.scrollLeft;
  }

  /** Returns the canvas Y position in the container's scrollable coordinate space. */
  private getCanvasOffsetY(): number {
    const canvas = this.pdfCanvas?.nativeElement;
    const container = this.pdfContainer?.nativeElement;
    if (!canvas || !container) return 0;

    return canvas.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
  }
}

/**
 * How clear a spotlight window is at `elapsedMs` into a flight, matching the stylesheet's states:
 * half for a dimmed window, fading out for one left behind, fading in for a new one (Issue #3127).
 */
export function flightWindowClearness(cutout: SpotlightCutout, elapsedMs: number): number {
  if (cutout.state === 'dimmed') return 0.5;
  if (cutout.state === 'leaving') return Math.max(0, 1 - elapsedMs / SPOTLIGHT_FADE_OUT_MS);
  if (cutout.entering) {
    const t = Math.min(1, Math.max(0, elapsedMs / SPOTLIGHT_FADE_IN_MS));
    return t * t * (3 - 2 * t);
  }
  return 1;
}
